import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft } from 'react-icons/fa';
import { StopChainMap } from '../../components/utility_planning/StopChainMap';
import { API_ENDPOINTS } from '../../config/utilityApiConfig';
import { isLocal } from '../../config/env';

const ALL_SEASONS = ['spring', 'summer', 'fall', 'winter'];
const DAILY_PROFILE_SEASONS = ['all', ...ALL_SEASONS];
const DAY_TYPE_OPTIONS = ['all', 'weekday', 'weekend'];
const HEX_PROFILE_CHART_COLOR = '#2563eb';

function totalMwh(hexLoadAggregate) {
    const totalKwh = (hexLoadAggregate || []).reduce((sum, hex) => sum + Number(hex?.energy_delivered || 0), 0);
    return totalKwh / 1000;
}

function formatChargingScenarioLabel(mode) {
    const key = String(mode || '').trim().toLowerCase();
    if (key === 'active') return 'Active Managed';
    if (key === 'passive') return 'Passive Managed';
    if (key === 'unmanaged') return 'Unmanaged';
    return key ? String(mode).trim() : 'Unmanaged';
}

function buildCaseDisplayTitle(caseData) {
    const utilityName = String(caseData?.utility_name || '').trim() || 'Utility';
    const rawScenario = String(caseData?.forecast_scenario || '').trim();
    const forecastScenario = rawScenario && rawScenario !== 'MDHD load forecast'
        ? rawScenario
        : 'Class 7-8 HD';
    const chargingScenario = String(caseData?.charging_scenario || '').trim();
    const chargingMode = caseData?.input_data?.mode || caseData?.result_data?.simulation_params?.mode;
    return `${utilityName} ${forecastScenario} ${chargingScenario || formatChargingScenarioLabel(chargingMode)}`;
}

function formatPacificTime(value) {
    if (!value) return '—';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZoneName: 'short',
        }).format(date);
    } catch (error) {
        return String(value);
    }
}

function formatTouPeriodLabel(tou) {
    if (!Array.isArray(tou) || tou.length === 0) return 'No TOU schedule';
    return tou
        .map((period) => {
            const start = Array.isArray(period) ? period[0] : period?.start;
            const end = Array.isArray(period) ? period[1] : period?.end;
            const price = Array.isArray(period) ? period[2] : period?.price;
            if (start == null || end == null || price == null) return null;
            return `${start}-${end}: $${Number(price).toFixed(4)}/kWh`;
        })
        .filter(Boolean)
        .join(' | ');
}

function formatMetric(value, digits = 2, suffix = '') {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toLocaleString(digits)}${suffix}`;
}

function computeLoadFactor(totalEnergyKwh, peakPowerKw, hourCount = 8760) {
    const energy = Number(totalEnergyKwh);
    const peak = Number(peakPowerKw);
    const hours = Number(hourCount);
    if (!Number.isFinite(energy) || !Number.isFinite(peak) || !Number.isFinite(hours) || peak <= 0 || hours <= 0) {
        return null;
    }
    return (energy / hours) / peak;
}

function formatWeekDateRange(startDate, endDate) {
    if (!startDate && !endDate) return '—';
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    const startLabel = start && !Number.isNaN(start.getTime())
        ? start.toLocaleDateString()
        : (startDate || '—');
    const endLabel = end && !Number.isNaN(end.getTime())
        ? end.toLocaleDateString()
        : (endDate || '—');
    return `${startLabel} - ${endLabel}`;
}

function celsiusToFahrenheit(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    return ((numericValue * 9) / 5) + 32;
}

function seasonFromDateValue(value) {
    if (!value) return 'unknown';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'unknown';
    const month = date.getUTCMonth() + 1;
    if ([3, 4, 5].includes(month)) return 'spring';
    if ([6, 7, 8].includes(month)) return 'summer';
    if ([9, 10, 11].includes(month)) return 'fall';
    return 'winter';
}

function buildTouHourlyPrices(tou) {
    const hourly = Array(24).fill(0);
    if (!Array.isArray(tou)) return hourly;
    tou.forEach((period) => {
        const start = Array.isArray(period) ? Number(period[0]) : Number(period?.start);
        const end = Array.isArray(period) ? Number(period[1]) : Number(period?.end);
        const price = Array.isArray(period) ? Number(period[2]) : Number(period?.price);
        if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(price)) return;
        for (let hour = Math.max(0, start); hour < Math.min(24, end); hour += 1) {
            hourly[hour] = price;
        }
    });
    return hourly;
}

function HourlyDiagnosticChart({
    values = [],
    color = HEX_PROFILE_CHART_COLOR,
    yLabel = 'Value',
    xLabel = 'Hour of day',
    yFormatter = (value) => Number(value || 0).toFixed(1),
}) {
    const safeValues = Array.isArray(values) ? values.slice(0, 24).map((value) => Number(value || 0)) : [];
    if (safeValues.length === 0) {
        return <div className="text-xs text-gray-500">No chart values available.</div>;
    }
    const width = 720;
    const height = 210;
    const padLeft = 58;
    const padRight = 16;
    const padTop = 10;
    const padBottom = 34;
    const innerW = width - (padLeft + padRight);
    const innerH = height - (padTop + padBottom);
    const maxVal = Math.max(...safeValues, 1e-9);
    const yTicks = [0, maxVal / 2, maxVal];
    const xTickHours = [0, 6, 12, 18, 23];
    const xForHour = (hour) => padLeft + (innerW * hour) / 23;
    const yForValue = (value) => padTop + innerH - ((value / maxVal) * innerH);
    const points = safeValues.map((value, index) => `${xForHour(index)},${yForValue(value)}`).join(' ');
    return (
        <div className="overflow-x-auto">
            <svg width={width} height={height} role="img" aria-label={yLabel}>
                {yTicks.map((tickValue, index) => {
                    const y = yForValue(tickValue);
                    return (
                        <g key={`diag-y-${index}`}>
                            <line x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x={padLeft - 8} y={y + 3} textAnchor="end" className="fill-gray-500 text-[10px]">
                                {yFormatter(tickValue)}
                            </text>
                        </g>
                    );
                })}
                {xTickHours.map((hour) => {
                    const x = xForHour(hour);
                    return (
                        <g key={`diag-x-${hour}`}>
                            <line x1={x} y1={padTop + innerH} x2={x} y2={padTop + innerH + 4} stroke="#9ca3af" strokeWidth="1" />
                            <text x={x} y={padTop + innerH + 16} textAnchor="middle" className="fill-gray-500 text-[10px]">
                                {`${String(hour).padStart(2, '0')}:00`}
                            </text>
                        </g>
                    );
                })}
                <line x1={padLeft} y1={padTop + innerH} x2={width - padRight} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
                <text x={16} y={padTop + (innerH / 2)} transform={`rotate(-90, 16, ${padTop + (innerH / 2)})`} textAnchor="middle" className="fill-gray-500 text-[10px]">
                    {yLabel}
                </text>
                <text x={padLeft + (innerW / 2)} y={height - 4} textAnchor="middle" className="fill-gray-500 text-[10px]">
                    {xLabel}
                </text>
            </svg>
        </div>
    );
}

function HexProfileMiniChart({
    values = [],
    color = HEX_PROFILE_CHART_COLOR,
    width = 320,
    height = 170,
    className = '',
}) {
    const safeValues = Array.isArray(values) ? values.slice(0, 24).map((v) => Number(v || 0)) : [];
    if (safeValues.length === 0) {
        return <div className="text-xs text-gray-500">No hourly profile values.</div>;
    }
    const padLeft = 58;
    const padRight = 18;
    const padTop = 10;
    const padBottom = 36;
    const innerW = width - (padLeft + padRight);
    const innerH = height - (padTop + padBottom);
    const maxVal = Math.max(...safeValues, 1e-9);
    const yTicks = [0, maxVal / 2, maxVal];
    const roundedTickValue = (value) => Math.round(Number(value || 0) / 100) * 100;
    const xTickHours = [0, 6, 12, 18, 23];
    const xForHour = (hour) => padLeft + (innerW * hour) / 23;
    const yForValue = (value) => padTop + innerH - ((value / maxVal) * innerH);
    const points = safeValues.map((value, idx) => `${xForHour(idx)},${yForValue(value)}`).join(' ');
    return (
        <div className={className}>
            <svg width={width} height={height} role="img" aria-label="Hexagon hourly load profile">
                {yTicks.map((tickValue, idx) => {
                    const y = yForValue(tickValue);
                    return (
                        <g key={`y-tick-${idx}`}>
                            <line x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <line x1={padLeft - 4} y1={y} x2={padLeft} y2={y} stroke="#9ca3af" strokeWidth="1" />
                            <text x={padLeft - 8} y={y + 3} textAnchor="end" className="fill-gray-500 text-[10px]">
                                {roundedTickValue(tickValue).toLocaleString()}
                            </text>
                        </g>
                    );
                })}
                {xTickHours.map((hour) => {
                    const x = xForHour(hour);
                    return (
                        <g key={`x-tick-${hour}`}>
                            <line x1={x} y1={padTop + innerH} x2={x} y2={padTop + innerH + 4} stroke="#9ca3af" strokeWidth="1" />
                            <text x={x} y={padTop + innerH + 16} textAnchor="middle" className="fill-gray-500 text-[10px]">
                                {`${String(hour).padStart(2, '0')}:00`}
                            </text>
                        </g>
                    );
                })}
                <line x1={padLeft} y1={padTop + innerH} x2={width - padRight} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
                <circle cx={xForHour(0)} cy={yForValue(safeValues[0])} r="2" fill={color} />
                <circle cx={xForHour(safeValues.length - 1)} cy={yForValue(safeValues[safeValues.length - 1])} r="2" fill={color} />
                <text
                    x={16}
                    y={padTop + (innerH / 2)}
                    transform={`rotate(-90, 16, ${padTop + (innerH / 2)})`}
                    textAnchor="middle"
                    className="fill-gray-500 text-[10px]"
                >
                    Hourly load (kWh)
                </text>
                <text
                    x={padLeft + (innerW / 2)}
                    y={height - 4}
                    textAnchor="middle"
                    className="fill-gray-500 text-[10px]"
                >
                    Hour of day
                </text>
            </svg>
        </div>
    );
}

function StopChargingProfileChart({ profilePoints = [] }) {
    const points = Array.isArray(profilePoints) ? profilePoints : [];
    if (!points.length) {
        return <div className="text-sm text-gray-500">No stop charging profile points available.</div>;
    }
    const values = points.map((point) => Number(point?.charging_power || 0));
    const maxVal = Math.max(...values, 1e-9);
    const width = 760;
    const height = 220;
    const padLeft = 58;
    const padRight = 16;
    const padTop = 14;
    const padBottom = 44;
    const innerW = width - (padLeft + padRight);
    const innerH = height - (padTop + padBottom);
    const xForIndex = (index) => padLeft + (innerW * index) / Math.max(values.length - 1, 1);
    const yForValue = (value) => padTop + innerH - ((value / maxVal) * innerH);
    const stepPoints = values.flatMap((value, index) => {
        const current = { x: xForIndex(index), y: yForValue(value) };
        if (index === 0) return [`${current.x},${current.y}`];
        const previousValue = values[index - 1];
        return [
            `${current.x},${yForValue(previousValue)}`,
            `${current.x},${current.y}`,
        ];
    }).join(' ');
    const firstTime = points[0]?.time ? formatPacificTime(points[0].time) : 'start';
    const lastTime = points[points.length - 1]?.time ? formatPacificTime(points[points.length - 1].time) : 'end';
    return (
        <div className="overflow-x-auto">
            <svg width={width} height={height} role="img" aria-label="Stop charging profile">
                {[0, maxVal / 2, maxVal].map((tick, index) => {
                    const y = yForValue(tick);
                    return (
                        <g key={`stop-profile-y-${index}`}>
                            <line x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x={padLeft - 8} y={y + 3} textAnchor="end" className="fill-gray-500 text-[10px]">
                                {Number(tick || 0).toFixed(0)}
                            </text>
                        </g>
                    );
                })}
                <line x1={padLeft} y1={padTop + innerH} x2={width - padRight} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <polyline fill="none" stroke="#f97316" strokeWidth="2" points={stepPoints} />
                <text x={padLeft} y={height - 18} textAnchor="start" className="fill-gray-500 text-[10px]">{firstTime}</text>
                <text x={width - padRight} y={height - 18} textAnchor="end" className="fill-gray-500 text-[10px]">{lastTime}</text>
                <text x={16} y={padTop + (innerH / 2)} transform={`rotate(-90, 16, ${padTop + (innerH / 2)})`} textAnchor="middle" className="fill-gray-500 text-[10px]">
                    Power (kW)
                </text>
            </svg>
        </div>
    );
}

function InteractiveTimelineChart({
    values = [],
    color = HEX_PROFILE_CHART_COLOR,
    width = 1400,
    height = 320,
    zoom = 1,
    xTickPositions = [],
    xTickLabels = [],
    ariaLabel = 'Profile timeline',
    xAxisLabel = 'Timeline',
    showWeekMarkers = false,
    weekMarkerIntervalHours = 24 * 7,
    weekMarkerMinLabelSpacingPx = 48,
    enableAdaptiveHourlyGrid = false,
    axisStartDate = null,
    hourlyBinByZoom = { low: 12, medium: 6, high: 3 },
    dateTickMinSpacingPx = 110,
}) {
    const scrollRef = useRef(null);
    const draggingRef = useRef(false);
    const dragStateRef = useRef({ startX: 0, startScrollLeft: 0 });
    const safeValues = Array.isArray(values) ? values.map((value) => Number(value || 0)) : [];

    useEffect(() => {
        const handleMouseUp = () => {
            draggingRef.current = false;
        };
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, []);

    if (safeValues.length === 0) {
        return <div className="text-sm text-gray-500">No profile values available.</div>;
    }

    const chartWidth = Math.max(width, Math.round(width * Number(zoom || 1)));
    const padLeft = 60;
    const padRight = 20;
    const padTop = 14;
    const padBottom = 42;
    const innerW = chartWidth - (padLeft + padRight);
    const innerH = height - (padTop + padBottom);
    const maxVal = Math.max(...safeValues, 1e-9);
    const yTicks = [0, maxVal / 2, maxVal];
    const xForIndex = (index) => padLeft + (innerW * index) / Math.max(safeValues.length - 1, 1);
    const yForValue = (value) => padTop + innerH - ((value / maxVal) * innerH);
    const points = safeValues.map((value, idx) => `${xForIndex(idx)},${yForValue(value)}`).join(' ');
    const areaPoints = `${padLeft},${padTop + innerH} ${points} ${chartWidth - padRight},${padTop + innerH}`;
    const resolvedAxisStartDate = (() => {
        if (!axisStartDate) return null;
        const value = axisStartDate instanceof Date ? axisStartDate : new Date(axisStartDate);
        return Number.isNaN(value.getTime()) ? null : value;
    })();
    const resolvedHourBin = (() => {
        if (!enableAdaptiveHourlyGrid) return null;
        const safeZoom = Number(zoom || 1);
        if (safeZoom >= 16) return Number(hourlyBinByZoom?.high || 3);
        if (safeZoom >= 8) return Number(hourlyBinByZoom?.medium || 6);
        return Number(hourlyBinByZoom?.low || 12);
    })();
    const adaptiveHourMarkers = [];
    const adaptiveDateTicks = [];
    if (enableAdaptiveHourlyGrid && resolvedAxisStartDate && safeValues.length > 1 && Number.isFinite(resolvedHourBin) && resolvedHourBin > 0) {
        const maxIndex = safeValues.length - 1;
        const binHours = Math.max(1, Math.round(resolvedHourBin));
        for (let hourIndex = 0; hourIndex <= maxIndex; hourIndex += binHours) {
            adaptiveHourMarkers.push({
                hourIndex,
                isMajor: (hourIndex % 24) === 0,
            });
        }
        const pxPerHour = innerW / Math.max(maxIndex, 1);
        const minTickHours = Math.max(binHours, Math.ceil(Number(dateTickMinSpacingPx || 110) / Math.max(pxPerHour, 1e-6)));
        const dateTickStepHours = Math.max(binHours, Math.ceil(minTickHours / binHours) * binHours);
        const dateLabelFormatter = new Intl.DateTimeFormat('en-US', {
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
            hour: dateTickStepHours < 24 ? 'numeric' : undefined,
            hour12: true,
        });
        for (let hourIndex = 0; hourIndex <= maxIndex; hourIndex += dateTickStepHours) {
            const tickDate = new Date(resolvedAxisStartDate);
            tickDate.setUTCHours(tickDate.getUTCHours() + hourIndex);
            adaptiveDateTicks.push({
                hourIndex,
                label: dateLabelFormatter.format(tickDate),
            });
        }
    }
    const weekMarkers = [];
    if (showWeekMarkers && Number.isFinite(Number(weekMarkerIntervalHours)) && Number(weekMarkerIntervalHours) > 0 && safeValues.length > 1) {
        const interval = Number(weekMarkerIntervalHours);
        const maxIndex = safeValues.length - 1;
        const pxPerWeek = (innerW * interval) / Math.max(maxIndex, 1);
        const effectiveLabelStep = Math.max(1, Math.ceil(Number(weekMarkerMinLabelSpacingPx) / Math.max(pxPerWeek, 1e-6)));
        const totalWeeks = Math.floor(maxIndex / interval);
        for (let weekIdx = 0; weekIdx <= totalWeeks; weekIdx += 1) {
            const hourIndex = Math.min(maxIndex, weekIdx * interval);
            const weekNumber = weekIdx + 1;
            weekMarkers.push({
                hourIndex,
                weekNumber,
                label: (() => {
                    if (!resolvedAxisStartDate) return `W${weekNumber}`;
                    const sundayDate = new Date(resolvedAxisStartDate);
                    sundayDate.setUTCHours(sundayDate.getUTCHours() + hourIndex);
                    return `Sun ${sundayDate.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}`;
                })(),
                showLabel: (weekIdx % effectiveLabelStep) === 0,
                isQuarterBoundary: weekIdx > 0 && (weekIdx % 13) === 0,
            });
        }
    }
    const handleMouseDown = (event) => {
        if (!scrollRef.current) return;
        draggingRef.current = true;
        dragStateRef.current = {
            startX: event.clientX,
            startScrollLeft: scrollRef.current.scrollLeft,
        };
    };
    const handleMouseMove = (event) => {
        if (!draggingRef.current || !scrollRef.current) return;
        const deltaX = event.clientX - dragStateRef.current.startX;
        scrollRef.current.scrollLeft = dragStateRef.current.startScrollLeft - deltaX;
    };

    return (
        <div className="space-y-2">
            <div className="text-[11px] text-gray-500">
                Drag horizontally inside the chart to inspect patterns in more detail.
            </div>
            <div
                ref={scrollRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                className="overflow-x-auto rounded border border-gray-200 bg-white cursor-grab active:cursor-grabbing select-none"
            >
                <svg width={chartWidth} height={height} className="h-[320px]" role="img" aria-label={ariaLabel}>
                    {yTicks.map((tickValue, idx) => {
                        const y = yForValue(tickValue);
                        return (
                            <g key={`interactive-y-${idx}`}>
                                <line x1={padLeft} y1={y} x2={chartWidth - padRight} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                                <text x={padLeft - 8} y={y + 3} textAnchor="end" className="fill-gray-500 text-[10px]">
                                    {Number(tickValue || 0).toFixed(0)}
                                </text>
                            </g>
                        );
                    })}
                    {(enableAdaptiveHourlyGrid ? adaptiveDateTicks.map((tick) => tick.hourIndex) : (xTickPositions || [])).map((index, idx) => {
                        const x = xForIndex(index);
                        const adaptiveTickLabel = enableAdaptiveHourlyGrid ? adaptiveDateTicks[idx]?.label : null;
                        return (
                            <g key={`interactive-x-${idx}`}>
                                <line x1={x} y1={padTop + innerH} x2={x} y2={padTop + innerH + 4} stroke="#9ca3af" strokeWidth="1" />
                                <text x={x} y={padTop + innerH + 16} textAnchor="middle" className="fill-gray-500 text-[10px]">
                                    {adaptiveTickLabel || xTickLabels[idx] || idx}
                                </text>
                            </g>
                        );
                    })}
                    <line x1={padLeft} y1={padTop + innerH} x2={chartWidth - padRight} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                    <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                    {adaptiveHourMarkers.map((marker) => {
                        const markerX = xForIndex(marker.hourIndex);
                        return (
                            <line
                                key={`hour-marker-${marker.hourIndex}`}
                                x1={markerX}
                                y1={padTop}
                                x2={markerX}
                                y2={padTop + innerH}
                                stroke={marker.isMajor ? '#cbd5e1' : '#e2e8f0'}
                                strokeWidth={marker.isMajor ? 1.1 : 0.9}
                                strokeDasharray={marker.isMajor ? '4 3' : '2 4'}
                            />
                        );
                    })}
                    {weekMarkers.map((marker) => {
                        const markerX = xForIndex(marker.hourIndex);
                        return (
                            <g key={`week-marker-${marker.weekNumber}`}>
                                <line
                                    x1={markerX}
                                    y1={padTop}
                                    x2={markerX}
                                    y2={padTop + innerH}
                                    stroke={marker.isQuarterBoundary ? '#93c5fd' : '#dbeafe'}
                                    strokeWidth={marker.isQuarterBoundary ? 1.4 : 1}
                                    strokeDasharray={marker.isQuarterBoundary ? '5 4' : '2 4'}
                                />
                                {marker.showLabel && (
                                    <text
                                        x={markerX + 2}
                                        y={padTop + 11}
                                        textAnchor="start"
                                        className="fill-blue-500 text-[9px] font-medium"
                                    >
                                        {marker.label}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                    <polygon points={areaPoints} fill="#93c5fd" fillOpacity="0.2" />
                    <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
                    <text
                        x={16}
                        y={padTop + (innerH / 2)}
                        transform={`rotate(-90, 16, ${padTop + (innerH / 2)})`}
                        textAnchor="middle"
                        className="fill-gray-500 text-[10px]"
                    >
                        Hourly load (kWh)
                    </text>
                    <text
                        x={padLeft + (innerW / 2)}
                        y={height - 6}
                        textAnchor="middle"
                        className="fill-gray-500 text-[10px]"
                    >
                        {xAxisLabel}
                    </text>
                </svg>
            </div>
        </div>
    );
}

function ProfileAnalyticsModal({
    open,
    onClose,
    title,
    profile,
    loading,
    error,
    seasonView,
    onSeasonViewChange,
    dayTypeView,
    onDayTypeViewChange,
}) {
    const [tab, setTab] = useState('yearly');
    const [timelineZoom, setTimelineZoom] = useState(2);

    useEffect(() => {
        if (!open) {
            setTab('yearly');
            setTimelineZoom(2);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    if (!open) return null;

    const hourlyProfile = Array.isArray(profile?.hourly_profile_8760_kwh) ? profile.hourly_profile_8760_kwh : [];
    const peakDayProfile = Array.isArray(profile?.peak_day_profile_24_kwh) ? profile.peak_day_profile_24_kwh : [];
    const seasonalProfiles = profile?.seasonal_average_daily_profiles_24_kwh || {};
    const seasonalWeeklyProfiles = buildSeasonalWeeklyProfiles(hourlyProfile, profile?.forecast_year);
    const weeklyProfile = seasonalWeeklyProfiles?.[seasonView]
        || seasonalWeeklyProfiles?.all
        || (Array.isArray(profile?.average_weekly_profile_168_kwh) ? profile.average_weekly_profile_168_kwh : []);
    const averageDailyValues = seasonalProfiles?.[seasonView]?.[dayTypeView]
        || seasonalProfiles?.all?.all
        || profile?.average_daily_profile_24_kwh
        || [];
    const yearlyTickPositions = [0, 2190, 4380, 6570, 8759];
    const yearlyTickLabels = ['Jan', 'Apr', 'Jul', 'Oct', 'Dec'];
    const weeklyTickPositions = [0, 24, 48, 72, 96, 120, 144, 167];
    const weeklyTickLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun 00:00', 'Sun 23:00'];
    const yearlyGridBinHours = timelineZoom >= 16 ? 3 : timelineZoom >= 8 ? 6 : 12;
    const yearlyAxisStartDate = new Date(Date.UTC(
        Number.isFinite(Number(profile?.forecast_year)) ? Number(profile?.forecast_year) : 2027,
        0,
        1,
        0,
        0,
        0
    ));
    const annualEnergyKwh = Number(profile?.annual_energy_kwh ?? profile?.profile_summary?.total_energy_kwh ?? 0);
    const yearlyPeakKw = Number(profile?.peak_power_kw ?? profile?.profile_summary?.peak_power_kw ?? 0);
    const loadFactor = computeLoadFactor(annualEnergyKwh, yearlyPeakKw);

    return (
        <div className="fixed inset-0 z-[1000] overflow-y-auto bg-gray-950/70 backdrop-blur-[1px]">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="w-full max-w-7xl max-h-[92vh] overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                    <div>
                        <div className="text-lg font-semibold text-gray-900">{title}</div>
                        <div className="text-xs text-gray-500 mt-1">
                            Explore yearly, weekly, seasonal, and peak-day load shapes without loading them into the main page payload.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Close
                    </button>
                </div>
                <div className="overflow-y-auto max-h-[calc(92vh-76px)] px-5 py-4 space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                        {[
                            ['yearly', '8760-hour'],
                            ['weekly', 'Weekly load shape'],
                            ['daily', 'Average daily'],
                            ['peakDay', 'Peak day'],
                        ].map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setTab(key)}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {tab === 'yearly' && (
                        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="flex flex-wrap items-center gap-3">
                                <label htmlFor="profile-zoom-slider" className="text-xs font-medium text-gray-700">
                                    Zoom
                                </label>
                                <input
                                    id="profile-zoom-slider"
                                    type="range"
                                    min={1}
                                    max={50}
                                    step={1}
                                    value={timelineZoom}
                                    onChange={(event) => setTimelineZoom(Number(event.target.value))}
                                    className="w-48"
                                />
                                <span className="text-xs text-gray-500">{timelineZoom.toFixed(2)}x</span>
                                <span className="text-xs text-blue-600">Grid: {yearlyGridBinHours}-hour bins</span>
                            </div>
                        </div>
                    )}
                    {loading ? (
                        <div className="text-sm text-gray-500">Loading analytics...</div>
                    ) : error ? (
                        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
                    ) : !profile ? (
                        <div className="text-sm text-gray-500">No analytics available.</div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                    <div className="text-xs uppercase text-gray-500">Annual energy</div>
                                    <div className="mt-1 text-lg font-semibold text-gray-800">{formatMetric(annualEnergyKwh / 1000, 2, ' MWh')}</div>
                                </div>
                                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                    <div className="text-xs uppercase text-gray-500">Yearly peak</div>
                                    <div className="mt-1 text-lg font-semibold text-gray-800">{formatMetric(yearlyPeakKw / 1000, 2, ' MW')}</div>
                                </div>
                                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                    <div className="text-xs uppercase text-gray-500">Load factor</div>
                                    <div className="mt-1 text-lg font-semibold text-gray-800">{loadFactor == null ? '—' : `${(loadFactor * 100).toFixed(1)}%`}</div>
                                </div>
                                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                    <div className="text-xs uppercase text-gray-500">Peak day</div>
                                    <div className="mt-1 text-lg font-semibold text-gray-800">{profile?.peak_day_date || '—'}</div>
                                </div>
                                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                    <div className="text-xs uppercase text-gray-500">Non-zero hours</div>
                                    <div className="mt-1 text-lg font-semibold text-gray-800">{profile?.profile_summary?.non_zero_hours ?? '—'}</div>
                                </div>
                            </div>

                            {tab === 'yearly' && (
                                <InteractiveTimelineChart
                                    values={hourlyProfile}
                                    zoom={timelineZoom}
                                    xTickPositions={yearlyTickPositions}
                                    xTickLabels={yearlyTickLabels}
                                    ariaLabel={`${title} 8760 hourly load profile`}
                                    xAxisLabel="Forecast year timeline"
                                    showWeekMarkers
                                    enableAdaptiveHourlyGrid
                                    axisStartDate={yearlyAxisStartDate}
                                />
                            )}

                            {tab === 'weekly' && (
                                <div className="space-y-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                        {DAILY_PROFILE_SEASONS.map((season) => (
                                            <button
                                                key={`weekly-${season}`}
                                                type="button"
                                                onClick={() => onSeasonViewChange(season)}
                                                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${seasonView === season ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                            >
                                                {season}
                                            </button>
                                        ))}
                                    </div>
                                    <InteractiveTimelineChart
                                        values={weeklyProfile}
                                        width={1200}
                                        xTickPositions={weeklyTickPositions}
                                        xTickLabels={weeklyTickLabels}
                                        ariaLabel={`${title} weekly load shape`}
                                        xAxisLabel={`Average week by hour (${seasonView})`}
                                    />
                                </div>
                            )}

                            {tab === 'daily' && (
                                <div className="space-y-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                        {DAILY_PROFILE_SEASONS.map((season) => (
                                            <button
                                                key={season}
                                                type="button"
                                                onClick={() => onSeasonViewChange(season)}
                                                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${seasonView === season ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                            >
                                                {season}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {DAY_TYPE_OPTIONS.map((dayType) => (
                                            <button
                                                key={dayType}
                                                type="button"
                                                onClick={() => onDayTypeViewChange(dayType)}
                                                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${dayTypeView === dayType ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                            >
                                                {dayType}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                        <div className="text-xs font-semibold text-gray-700 mb-2 capitalize">
                                            Average daily profile: {seasonView} / {dayTypeView}
                                        </div>
                                        <HexProfileMiniChart values={averageDailyValues} width={960} height={240} className="overflow-x-auto" />
                                    </div>
                                </div>
                            )}

                            {tab === 'peakDay' && (
                                <div className="space-y-3">
                                    <div className="text-sm text-gray-700">
                                        Peak day: <span className="font-semibold">{profile?.peak_day_date || '—'}</span>
                                        {' | '}
                                        Peak hour load: <span className="font-semibold">{formatMetric(profile?.peak_day_peak_kw, 2, ' kWh')}</span>
                                    </div>
                                    <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                        <HexProfileMiniChart values={peakDayProfile} width={960} height={240} className="overflow-x-auto" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                </div>
            </div>
        </div>
    );
}

function formatBackfillStatusLabel(snapshot) {
    if (snapshot?.backfill_unrecoverable) return 'Unrecoverable';
    if (snapshot?.backfill_applied) return 'Applied';
    if (Number(snapshot?.missing_feasible_count || 0) > 0) return 'Needed';
    return 'Not needed';
}

function normalizeMapLayerItems(layerData) {
    if (Array.isArray(layerData)) return layerData;
    if (Array.isArray(layerData?.features)) return layerData.features;
    return [];
}

function MapLayerIcon({ type }) {
    if (type === 'public_chargers') {
        return <span className="inline-block h-3 w-3 rounded-full bg-green-500 border border-green-800" />;
    }
    if (type === 'mdhd_infrastructure') {
        return <span className="inline-block text-violet-700 text-xs leading-none">◀</span>;
    }
    if (type === 'warehouses') {
        return <span className="inline-block h-3 w-3 rotate-45 bg-amber-500 border border-amber-800" />;
    }
    if (type === 'truck_stops') {
        return <span className="inline-block h-3 w-3 rounded-full bg-orange-500 border border-orange-900" />;
    }
    return null;
}

function buildFallbackSnapshot(resultData) {
    const reachedYears = Array.isArray(resultData?.forecast_years_reached) ? resultData.forecast_years_reached : [];
    const forecastTargets = Array.isArray(resultData?.forecast_targets) ? resultData.forecast_targets : [];
    const lastReachedYear = reachedYears.length > 0 ? reachedYears[reachedYears.length - 1] : null;
    const matchingTarget = forecastTargets.find((target) => Number(target?.year) === Number(lastReachedYear));
    return {
        year: lastReachedYear,
        target_ev_count: matchingTarget?.target_ev_count ?? null,
        hex_load_aggregate: Array.isArray(resultData?.hex_load_aggregate) ? resultData.hex_load_aggregate : [],
    };
}

function seasonForMonth(month) {
    const monthNumber = Number(month);
    if ([3, 4, 5].includes(monthNumber)) return 'spring';
    if ([6, 7, 8].includes(monthNumber)) return 'summer';
    if ([9, 10, 11].includes(monthNumber)) return 'fall';
    return 'winter';
}

function buildSeasonalWeeklyProfiles(hourlyProfile, forecastYear) {
    const safeValues = Array.isArray(hourlyProfile) ? hourlyProfile.map((value) => Number(value || 0)) : [];
    if (safeValues.length === 0) {
        return { all: [] };
    }

    const HOURS_PER_DAY = 24;
    const HOURS_PER_WEEK = 24 * 7;
    const baseYear = Number.isFinite(Number(forecastYear)) ? Number(forecastYear) : 2027;
    const startDate = new Date(Date.UTC(baseYear, 0, 1));
    const buckets = {
        all: { totals: Array(HOURS_PER_WEEK).fill(0), counts: Array(HOURS_PER_WEEK).fill(0) },
        spring: { totals: Array(HOURS_PER_WEEK).fill(0), counts: Array(HOURS_PER_WEEK).fill(0) },
        summer: { totals: Array(HOURS_PER_WEEK).fill(0), counts: Array(HOURS_PER_WEEK).fill(0) },
        fall: { totals: Array(HOURS_PER_WEEK).fill(0), counts: Array(HOURS_PER_WEEK).fill(0) },
        winter: { totals: Array(HOURS_PER_WEEK).fill(0), counts: Array(HOURS_PER_WEEK).fill(0) },
    };

    safeValues.forEach((value, hourIndex) => {
        const dayOffset = Math.floor(hourIndex / HOURS_PER_DAY);
        const hourOfDay = hourIndex % HOURS_PER_DAY;
        const currentDate = new Date(startDate);
        currentDate.setUTCDate(startDate.getUTCDate() + dayOffset);
        const dayOfWeek = currentDate.getUTCDay();
        const mondayBasedDay = (dayOfWeek + 6) % 7;
        const weekHourIndex = (mondayBasedDay * HOURS_PER_DAY) + hourOfDay;
        const season = seasonForMonth(currentDate.getUTCMonth() + 1);

        buckets.all.totals[weekHourIndex] += value;
        buckets.all.counts[weekHourIndex] += 1;
        buckets[season].totals[weekHourIndex] += value;
        buckets[season].counts[weekHourIndex] += 1;
    });

    return Object.fromEntries(
        Object.entries(buckets).map(([season, bucket]) => [
            season,
            bucket.totals.map((total, index) => (
                bucket.counts[index] > 0 ? Number((total / bucket.counts[index]).toFixed(6)) : 0
            )),
        ])
    );
}

function combineHexLoadAggregates(hexLoadGroups) {
    const combined = new Map();
    (hexLoadGroups || []).forEach((rows) => {
        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const h3Index = String(row?.h3_index || '').trim();
            if (!h3Index) return;
            const existing = combined.get(h3Index);
            const energyDelivered = Number(row?.energy_delivered || 0);
            const peakPowerKw = Number(row?.peak_power_kw || 0);
            if (!existing) {
                combined.set(h3Index, {
                    ...(row || {}),
                    h3_index: h3Index,
                    energy_delivered: Number.isFinite(energyDelivered) ? energyDelivered : 0,
                    peak_power_kw: Number.isFinite(peakPowerKw) ? peakPowerKw : 0,
                });
                return;
            }
            existing.energy_delivered += Number.isFinite(energyDelivered) ? energyDelivered : 0;
            existing.peak_power_kw = Math.max(existing.peak_power_kw || 0, Number.isFinite(peakPowerKw) ? peakPowerKw : 0);
        });
    });
    return Array.from(combined.values());
}

function normalizeSeasonResultData(resultData) {
    const payload = resultData && typeof resultData === 'object' ? resultData : {};
    const topLevelHexes = Array.isArray(payload?.hex_load_aggregate) ? payload.hex_load_aggregate : [];
    const existingYearSnapshots = Array.isArray(payload?.year_snapshots) ? payload.year_snapshots : [];
    const weekSnapshots = Array.isArray(payload?.week_snapshots) ? payload.week_snapshots : [];
    if (existingYearSnapshots.length > 0 || weekSnapshots.length === 0) {
        return payload;
    }

    const forecastTargets = Array.isArray(payload?.forecast_targets) ? payload.forecast_targets : [];
    const targetByYear = new Map(
        forecastTargets
            .map((target) => [Number(target?.year), target?.target_ev_count])
            .filter(([year]) => Number.isFinite(year))
    );
    const weeksByYear = new Map();
    weekSnapshots.forEach((snapshot) => {
        const year = Number(snapshot?.year);
        if (!Number.isFinite(year)) return;
        if (!weeksByYear.has(year)) {
            weeksByYear.set(year, []);
        }
        weeksByYear.get(year).push(snapshot);
    });

    const syntheticYearSnapshots = Array.from(weeksByYear.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([year, snapshots]) => ({
            year,
            target_ev_count: targetByYear.get(year) ?? null,
            hex_load_aggregate: combineHexLoadAggregates(snapshots.map((snapshot) => snapshot?.hex_load_aggregate || [])),
            week_snapshots: snapshots,
        }));

    const syntheticTopLevelHexes = topLevelHexes.length > 0
        ? topLevelHexes
        : combineHexLoadAggregates(syntheticYearSnapshots.map((snapshot) => snapshot?.hex_load_aggregate || []));
    const forecastYearsReached = syntheticYearSnapshots.map((snapshot) => snapshot.year).filter((year) => Number.isFinite(year));

    return {
        ...payload,
        hex_load_aggregate: syntheticTopLevelHexes,
        year_snapshots: syntheticYearSnapshots,
        forecast_years_reached: forecastYearsReached,
    };
}

function UtilityScenarioCaseDetailPage() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState('all');
    const [selectedYear, setSelectedYear] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
    const [showChargersOnMap, setShowChargersOnMap] = useState(false);
    const [showMdhdInfrastructure, setShowMdhdInfrastructure] = useState(false);
    const [showWarehouses, setShowWarehouses] = useState(false);
    const [showTruckStops, setShowTruckStops] = useState(false);
    const [selectedHexagonH3Index, setSelectedHexagonH3Index] = useState('');
    const [heatmapLayerMetric, setHeatmapLayerMetric] = useState('energy_delivered');
    const [selectedHexProfile, setSelectedHexProfile] = useState(null);
    const [selectedHexProfileLoading, setSelectedHexProfileLoading] = useState(false);
    const [selectedHexProfileError, setSelectedHexProfileError] = useState('');
    const [utilityWideProfile, setUtilityWideProfile] = useState(null);
    const [utilityWideProfileLoading, setUtilityWideProfileLoading] = useState(false);
    const [utilityWideProfileError, setUtilityWideProfileError] = useState('');
    const [analyticsModalTarget, setAnalyticsModalTarget] = useState(null);
    const [analyticsSeasonView, setAnalyticsSeasonView] = useState('all');
    const [analyticsDayTypeView, setAnalyticsDayTypeView] = useState('all');
    const [epriValidationReport, setEpriValidationReport] = useState(null);
    const [epriValidationLoading, setEpriValidationLoading] = useState(false);
    const [epriValidationError, setEpriValidationError] = useState('');
    const [stopProfiles, setStopProfiles] = useState([]);
    const [stopProfilesMeta, setStopProfilesMeta] = useState(null);
    const [stopProfilesLoading, setStopProfilesLoading] = useState(false);
    const [stopProfilesError, setStopProfilesError] = useState('');
    const [stopProfileSeasonFilter, setStopProfileSeasonFilter] = useState('all');
    const [selectedStopProfileId, setSelectedStopProfileId] = useState('');
    const [selectedStopProfile, setSelectedStopProfile] = useState(null);
    const [selectedStopProfileLoading, setSelectedStopProfileLoading] = useState(false);
    const [selectedStopProfileError, setSelectedStopProfileError] = useState('');

    useEffect(() => {
        const run = async () => {
            setLoading(true);
            setError('');
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(`${API_ENDPOINTS.mdhdAnalysisCases}${caseId}/`, {
                    headers: token ? { Authorization: `Token ${token}` } : {},
                });
                const payload = response.data || null;
                setCaseData(payload);
                const seasonResults = payload?.result_data?.season_results || {};
                const seasonKeys = Object.keys(seasonResults);
                setSelectedSeason('all');
                const snapshots = Array.isArray(payload?.result_data?.year_snapshots) ? payload.result_data.year_snapshots : [];
                if (snapshots.length > 0) {
                    setSelectedYear(String(snapshots[snapshots.length - 1]?.year ?? ''));
                } else {
                    const fallbackSnapshot = buildFallbackSnapshot(payload?.result_data);
                    if (fallbackSnapshot?.year != null) {
                        setSelectedYear(String(fallbackSnapshot.year));
                    } else if (seasonKeys.length > 0) {
                        const firstSeason = seasonKeys[0];
                        const firstSeasonFallback = buildFallbackSnapshot(seasonResults[firstSeason]);
                        if (firstSeasonFallback?.year != null) {
                            setSelectedSeason(firstSeason);
                            setSelectedYear(String(firstSeasonFallback.year));
                        }
                    }
                }
            } catch (err) {
                setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to load saved analysis.');
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [caseId]);

    const seasonResults = useMemo(() => caseData?.result_data?.season_results || {}, [caseData]);
    const availableSeasonViews = useMemo(() => {
        const seasonKeys = Object.keys(seasonResults);
        if (!seasonKeys.length) return [{ key: 'all', label: 'All seasons' }];
        return [
            { key: 'all', label: 'All seasons' },
            ...seasonKeys.map((season) => ({
                key: season,
                label: season.charAt(0).toUpperCase() + season.slice(1),
            })),
        ];
    }, [seasonResults]);
    const normalizedSeasonResults = useMemo(() => (
        Object.fromEntries(
            Object.entries(seasonResults).map(([season, payload]) => [season, normalizeSeasonResultData(payload)])
        )
    ), [seasonResults]);
    const activeResultData = useMemo(() => {
        if (selectedSeason !== 'all' && normalizedSeasonResults[selectedSeason]) {
            return normalizedSeasonResults[selectedSeason];
        }
        return caseData?.result_data || {};
    }, [caseData, normalizedSeasonResults, selectedSeason]);
    const yearSnapshots = useMemo(() => (
        Array.isArray(activeResultData?.year_snapshots) ? activeResultData.year_snapshots : []
    ), [activeResultData]);

    const fallbackSnapshot = useMemo(() => buildFallbackSnapshot(activeResultData), [activeResultData]);
    const engineDebug = useMemo(() => {
        const simulationParams = activeResultData?.simulation_params || caseData?.result_data?.simulation_params || {};
        const engineRollout = simulationParams?.engine_rollout || {};
        const engineRuntimeStats = simulationParams?.engine_runtime_stats || {};
        const contractValidation = simulationParams?.contract_validation || {};
        const shortfallBackfill = simulationParams?.shortfall_backfill || {};
        const requested = simulationParams?.simulation_engine_requested || 'python';
        const used = simulationParams?.simulation_engine_used || 'python';
        const engineVariant = simulationParams?.simulation_engine_variant || used;
        const hexProfileCoverage = simulationParams?.hex_profile_coverage || {};
        const fallbackReason = engineRollout?.fallback_reason || null;
        return {
            requested,
            used,
            engineVariant,
            hexProfileCoverage,
            fallbackReason,
            rolloutPercent: engineRollout?.rollout?.rollout_percent,
            rolloutBucket: engineRollout?.rollout?.rollout_bucket,
            rustEnabled: engineRollout?.rollout?.rust_enabled,
            rustPointerEnabled: engineRollout?.rollout?.rust_pointer_enabled,
            contractOk: contractValidation?.ok,
            engineRuntimeStats,
            shortfallBackfill,
        };
    }, [activeResultData, caseData]);

    const selectedSnapshot = useMemo(() => {
        const wanted = Number(selectedYear);
        return yearSnapshots.find((snap) => Number(snap?.year) === wanted) || yearSnapshots[yearSnapshots.length - 1] || fallbackSnapshot;
    }, [selectedYear, yearSnapshots, fallbackSnapshot]);
    const heatmapMaxValueOverride = useMemo(() => {
        const snapshots = (Array.isArray(yearSnapshots) && yearSnapshots.length > 0)
            ? yearSnapshots
            : (fallbackSnapshot ? [fallbackSnapshot] : []);
        let maxValue = 0;
        for (const snapshot of snapshots) {
            const hexRows = Array.isArray(snapshot?.hex_load_aggregate) ? snapshot.hex_load_aggregate : [];
            for (const row of hexRows) {
                const candidate = heatmapLayerMetric === 'peak_power_kw'
                    ? Number(row?.peak_power_kw || 0)
                    : Number(row?.energy_delivered || 0);
                if (Number.isFinite(candidate) && candidate > maxValue) {
                    maxValue = candidate;
                }
            }
        }
        return maxValue > 0 ? maxValue : null;
    }, [yearSnapshots, fallbackSnapshot, heatmapLayerMetric]);

    const devTabEnabled = isLocal;
    const activeDevData = useMemo(() => {
        const baseDevData = caseData?.result_data?.dev_data || null;
        if (selectedSeason !== 'all' && normalizedSeasonResults[selectedSeason]?.dev_data) {
            return {
                ...(baseDevData || {}),
                ...(normalizedSeasonResults[selectedSeason].dev_data || {}),
            };
        }
        return baseDevData;
    }, [caseData, normalizedSeasonResults, selectedSeason]);
    const hasDevTab = devTabEnabled;
    const trajectoryPreview = useMemo(() => (
        Array.isArray(activeDevData?.trajectory_results_preview) ? activeDevData.trajectory_results_preview : []
    ), [activeDevData]);
    const weatherDatabaseSummary = activeDevData?.weather_database_summary || null;
    const selectedPreview = useMemo(() => (
        trajectoryPreview[selectedPreviewIndex] || trajectoryPreview[0] || null
    ), [trajectoryPreview, selectedPreviewIndex]);
    const devPreviewDiagnostics = useMemo(() => {
        const hourlyTouTotals = Array(24).fill(0);
        const hourlyTouCounts = Array(24).fill(0);
        const hourlyChargeEnergy = Array(24).fill(0);
        const hourlyChargeStops = Array(24).fill(0);
        const seasonTemperature = {
            winter: [],
            spring: [],
            summer: [],
            fall: [],
        };
        let totalChargingStops = 0;
        let totalChargingEnergyKwh = 0;
        let depotChargingStops = 0;
        let publicChargingStops = 0;
        let trajectoriesWithCharging = 0;
        const utilityCounts = new Map();
        const rateCounts = new Map();

        trajectoryPreview.forEach((preview) => {
            const touRows = Array.isArray(preview?.charging_stop_tou) ? preview.charging_stop_tou : [];
            const stops = Array.isArray(preview?.stops) ? preview.stops : [];
            const weatherRows = Array.isArray(preview?.stop_weather) ? preview.stop_weather : [];
            if (touRows.length > 0) {
                trajectoriesWithCharging += 1;
            }
            touRows.forEach((touRow) => {
                const touHourly = buildTouHourlyPrices(touRow?.tou);
                touHourly.forEach((price, hour) => {
                    if (Number.isFinite(price)) {
                        hourlyTouTotals[hour] += price;
                        hourlyTouCounts[hour] += 1;
                    }
                });
                const energyDelivered = Number(touRow?.energy_delivered || 0);
                totalChargingStops += 1;
                totalChargingEnergyKwh += Number.isFinite(energyDelivered) ? energyDelivered : 0;
                const utilityRegion = String(touRow?.utility_region || '').trim();
                const rateName = String(touRow?.tou_rate_name || 'Default TOU').trim();
                if (utilityRegion) {
                    utilityCounts.set(utilityRegion, (utilityCounts.get(utilityRegion) || 0) + 1);
                }
                if (rateName) {
                    rateCounts.set(rateName, (rateCounts.get(rateName) || 0) + 1);
                }
                const stopIndex = Number(touRow?.stop_index);
                const stop = Number.isFinite(stopIndex) ? stops[stopIndex] : null;
                if (stop?.is_depot) {
                    depotChargingStops += 1;
                } else {
                    publicChargingStops += 1;
                }
                const stopTime = stop?.arrival_time ? new Date(stop.arrival_time) : null;
                if (stopTime && !Number.isNaN(stopTime.getTime())) {
                    hourlyChargeEnergy[stopTime.getHours()] += Number.isFinite(energyDelivered) ? energyDelivered : 0;
                    hourlyChargeStops[stopTime.getHours()] += 1;
                }
            });
            weatherRows.forEach((entry) => {
                const season = seasonFromDateValue(entry?.arrival_time || entry?.departure_time);
                const segmentTempF = Number.isFinite(Number(entry?.segment_avg_temp_to_next_f))
                    ? Number(entry?.segment_avg_temp_to_next_f)
                    : celsiusToFahrenheit(entry?.segment_avg_temp_to_next_c);
                if (seasonTemperature[season] && Number.isFinite(segmentTempF)) {
                    seasonTemperature[season].push(segmentTempF);
                }
            });
        });

        const averageTouByHour = hourlyTouTotals.map((total, hour) => (
            hourlyTouCounts[hour] > 0 ? Number((total / hourlyTouCounts[hour]).toFixed(6)) : 0
        ));
        const topUtilities = Array.from(utilityCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
        const topRates = Array.from(rateCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
        const seasonalTemperatureSummary = Object.fromEntries(
            Object.entries(seasonTemperature).map(([season, values]) => {
                if (!values.length) {
                    return [season, { avgF: null, minF: null, maxF: null, sampleCount: 0 }];
                }
                const avgF = values.reduce((sum, value) => sum + value, 0) / values.length;
                return [season, {
                    avgF: Number(avgF.toFixed(2)),
                    minF: Number(Math.min(...values).toFixed(2)),
                    maxF: Number(Math.max(...values).toFixed(2)),
                    sampleCount: values.length,
                }];
            })
        );

        return {
            totalChargingStops,
            totalChargingEnergyKwh: Number(totalChargingEnergyKwh.toFixed(3)),
            trajectoriesWithCharging,
            avgChargingStopsPerTrajectory: trajectoryPreview.length > 0
                ? Number((totalChargingStops / trajectoryPreview.length).toFixed(2))
                : null,
            avgEnergyPerChargingStopKwh: totalChargingStops > 0
                ? Number((totalChargingEnergyKwh / totalChargingStops).toFixed(2))
                : null,
            depotChargingStops,
            publicChargingStops,
            averageTouByHour,
            hourlyChargeEnergy,
            hourlyChargeStops,
            topUtilities,
            topRates,
            seasonalTemperatureSummary,
        };
    }, [trajectoryPreview]);
    const stopProfileSummary = useMemo(() => {
        const rows = Array.isArray(stopProfiles) ? stopProfiles : [];
        const totalEnergy = rows.reduce((sum, row) => sum + Number(row?.energy_delivered_kwh || 0), 0);
        const depotCount = rows.filter((row) => row?.is_depot).length;
        const peakPower = rows.reduce((maxValue, row) => Math.max(maxValue, Number(row?.peak_power_kw || 0)), 0);
        return {
            count: rows.length,
            totalEnergyKwh: totalEnergy,
            depotCount,
            publicCount: rows.length - depotCount,
            peakPowerKw: peakPower,
        };
    }, [stopProfiles]);
    const savedDebugInfo = caseData?.result_data?.debug_info || {};

    useEffect(() => {
        setSelectedPreviewIndex(0);
        setSelectedStopProfileId('');
    }, [selectedSeason, caseData?.id]);

    useEffect(() => {
        setSelectedStopProfileId('');
    }, [selectedSnapshot?.year]);

    useEffect(() => {
        setSelectedStopProfileId('');
    }, [stopProfileSeasonFilter]);

    useEffect(() => {
        if (!hasDevTab && activeTab !== 'overview') {
            setActiveTab('overview');
        }
    }, [activeTab, hasDevTab]);

    useEffect(() => {
        if (!hasDevTab || activeTab !== 'dev' || !caseData?.id || !selectedSnapshot?.year) {
            setStopProfiles([]);
            setStopProfilesMeta(null);
            setStopProfilesError('');
            setStopProfilesLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setStopProfilesLoading(true);
            setStopProfilesError('');
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(API_ENDPOINTS.mdhdAnalysisCaseStopProfiles(caseData.id), {
                    params: {
                        year: selectedSnapshot.year,
                        season: stopProfileSeasonFilter,
                        limit: 1000,
                    },
                    headers: token ? { Authorization: `Token ${token}` } : {},
                });
                if (!cancelled) {
                    setStopProfiles(Array.isArray(response.data?.stop_profiles) ? response.data.stop_profiles : []);
                    setStopProfilesMeta(response.data || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setStopProfiles([]);
                    setStopProfilesMeta(null);
                    setStopProfilesError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to load charging stop profiles.');
                }
            } finally {
                if (!cancelled) {
                    setStopProfilesLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [activeTab, caseData?.id, hasDevTab, selectedSnapshot?.year, stopProfileSeasonFilter]);

    useEffect(() => {
        if (!caseData?.id || !selectedStopProfileId) {
            setSelectedStopProfile(null);
            setSelectedStopProfileError('');
            setSelectedStopProfileLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setSelectedStopProfileLoading(true);
            setSelectedStopProfileError('');
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(API_ENDPOINTS.mdhdAnalysisCaseStopProfileDetail(caseData.id, selectedStopProfileId), {
                    headers: token ? { Authorization: `Token ${token}` } : {},
                });
                if (!cancelled) {
                    setSelectedStopProfile(response.data || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setSelectedStopProfile(null);
                    setSelectedStopProfileError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to load charging stop profile.');
                }
            } finally {
                if (!cancelled) {
                    setSelectedStopProfileLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [caseData?.id, selectedStopProfileId]);

    const selectedHexes = selectedSnapshot?.hex_load_aggregate || [];
    const selectedHexagon = useMemo(() => {
        if (!Array.isArray(selectedHexes) || selectedHexes.length === 0) return null;
        const selected = selectedHexes.find((hex) => String(hex?.h3_index) === String(selectedHexagonH3Index));
        return selected || null;
    }, [selectedHexes, selectedHexagonH3Index]);
    const activeAnalyticsProfile = analyticsModalTarget === 'utility' ? utilityWideProfile : selectedHexProfile;
    const activeAnalyticsLoading = analyticsModalTarget === 'utility' ? utilityWideProfileLoading : selectedHexProfileLoading;
    const activeAnalyticsError = analyticsModalTarget === 'utility' ? utilityWideProfileError : selectedHexProfileError;
    const activeAnalyticsTitle = analyticsModalTarget === 'utility'
        ? `Utility-wide load analytics${selectedSnapshot?.year ? ` (${selectedSnapshot.year})` : ''}`
        : `Hexagon load analytics${selectedHexagon?.h3_index ? ` (${selectedHexagon.h3_index})` : ''}`;

    useEffect(() => {
        if (!caseData?.id || !selectedHexagonH3Index || !selectedSnapshot?.year) {
            setSelectedHexProfile(null);
            setSelectedHexProfileError('');
            setSelectedHexProfileLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setSelectedHexProfileLoading(true);
            setSelectedHexProfileError('');
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(API_ENDPOINTS.mdhdAnalysisCaseHexProfile(caseData.id), {
                    params: {
                        year: selectedSnapshot.year,
                        h3_index: selectedHexagonH3Index,
                        season: selectedSeason,
                    },
                    headers: token ? { Authorization: `Token ${token}` } : {},
                });
                if (!cancelled) {
                    setSelectedHexProfile(response.data || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setSelectedHexProfile(null);
                    setSelectedHexProfileError(
                        err.response?.data?.detail
                        || err.response?.data?.error
                        || err.message
                        || 'Failed to load hex profile.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setSelectedHexProfileLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [caseData?.id, selectedHexagonH3Index, selectedSnapshot?.year]);

    useEffect(() => {
        if (!caseData?.id || !selectedSnapshot?.year) {
            setUtilityWideProfile(null);
            setUtilityWideProfileError('');
            setUtilityWideProfileLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setUtilityWideProfileLoading(true);
            setUtilityWideProfileError('');
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(API_ENDPOINTS.mdhdAnalysisCaseUtilityProfile(caseData.id), {
                    params: {
                        year: selectedSnapshot.year,
                    },
                    headers: token ? { Authorization: `Token ${token}` } : {},
                });
                if (!cancelled) {
                    setUtilityWideProfile(response.data || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setUtilityWideProfile(null);
                    setUtilityWideProfileError(
                        err.response?.data?.detail
                        || err.response?.data?.error
                        || err.message
                        || 'Failed to load utility-wide profile.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setUtilityWideProfileLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [caseData?.id, selectedSnapshot?.year]);

    useEffect(() => {
        if (!isLocal || !caseData?.id || !selectedSnapshot?.year) {
            setEpriValidationReport(null);
            setEpriValidationError('');
            setEpriValidationLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setEpriValidationLoading(true);
            setEpriValidationError('');
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(API_ENDPOINTS.mdhdAnalysisCaseEpriValidation(caseData.id), {
                    params: {
                        year: selectedSnapshot.year,
                    },
                    headers: token ? { Authorization: `Token ${token}` } : {},
                });
                if (!cancelled) {
                    setEpriValidationReport(response.data || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setEpriValidationReport(null);
                    const responseData = err.response?.data || {};
                    const missingDatasetDetails = responseData.fetch_command
                        ? ` Run: ${responseData.fetch_command}`
                        : responseData.expected_path
                            ? ` Expected cache path: ${responseData.expected_path}`
                            : '';
                    setEpriValidationError(
                        `${responseData.error
                        || responseData.detail
                        || err.message
                        || 'Failed to load EPRI validation report.'}${missingDatasetDetails}`
                    );
                }
            } finally {
                if (!cancelled) {
                    setEpriValidationLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [caseData?.id, selectedSnapshot?.year]);

    useEffect(() => {
        setAnalyticsSeasonView('all');
        setAnalyticsDayTypeView('all');
    }, [analyticsModalTarget, selectedSnapshot?.year, selectedHexagonH3Index]);

    useEffect(() => {
        if (!Array.isArray(selectedHexes) || selectedHexes.length === 0) {
            setSelectedHexagonH3Index('');
            return;
        }
        if (selectedHexagonH3Index && !selectedHexes.some((hex) => String(hex?.h3_index) === String(selectedHexagonH3Index))) {
            setSelectedHexagonH3Index('');
        }
    }, [selectedHexes, selectedHexagonH3Index]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-ui">Loading saved analysis...</p>
                </div>
            </div>
        );
    }

    if (error || !caseData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
                <div className="text-center bg-white p-12 rounded-lg shadow-xl max-w-lg">
                    <h2 className="text-3xl font-bold text-gray-800 mb-4 font-ui">Case Not Found</h2>
                    <p className="text-gray-600 mb-8 font-ui">{error || 'The requested saved analysis could not be found.'}</p>
                    <button
                        className="bg-blue-600 hover:bg-blue-700 text-white font-ui font-bold py-3 px-6 rounded-md transition duration-300"
                        onClick={() => navigate('/utility/scenarios')}
                    >
                        Back to Case Library
                    </button>
                </div>
            </div>
        );
    }

    const handleDeleteCase = async () => {
        if (!caseData?.id) return;
        const confirmed = window.confirm(`Delete saved case "${buildCaseDisplayTitle(caseData)}"?`);
        if (!confirmed) return;
        setDeleting(true);
        try {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${API_ENDPOINTS.mdhdAnalysisCases}${caseData.id}/`, {
                headers: token ? { Authorization: `Token ${token}` } : {},
            });
            navigate('/utility/scenarios');
        } catch (err) {
            window.alert(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to delete saved case.');
            setDeleting(false);
        }
    };

    const selectedWeekSnapshots = Array.isArray(selectedSnapshot?.week_snapshots) ? selectedSnapshot.week_snapshots : [];
    const peakWeekAttribution = selectedWeekSnapshots.reduce((best, weekSnapshot) => {
        const weekHexes = Array.isArray(weekSnapshot?.hex_load_aggregate)
            ? weekSnapshot.hex_load_aggregate
            : [];
        const peakPowerKw = weekHexes.reduce((sum, row) => sum + Number(row?.peak_power_kw || 0), 0);
        const weekLoadMwh = weekHexes.length > 0
            ? totalMwh(weekHexes)
            : Number(weekSnapshot?.week_total_load_mwh || 0);
        const topHex = weekHexes.reduce((top, row) => (
            Number(row?.peak_power_kw || 0) > Number(top?.peak_power_kw || 0) ? row : top
        ), null);
        const candidate = {
            weekSnapshot,
            peakPowerKw,
            weekLoadMwh,
            topHex,
            loadFactor: computeLoadFactor(
                weekLoadMwh * 1000,
                peakPowerKw,
                Math.max(1, Number(weekSnapshot?.coverage_days || 7) * 24),
            ),
        };
        if (!best) return candidate;
        if (peakPowerKw > best.peakPowerKw) return candidate;
        if (peakPowerKw === best.peakPowerKw && weekLoadMwh > best.weekLoadMwh) return candidate;
        return best;
    }, null);
    const selectedYearBackfillSummary = {
        missingFeasibleCount: Number(selectedSnapshot?.missing_feasible_count || 0),
        resampledChainCount: Number(selectedSnapshot?.resampled_chain_count || 0),
        weeksWithBackfill: Number(selectedSnapshot?.weeks_with_backfill || 0),
        weeksUnrecoverable: Number(selectedSnapshot?.weeks_unrecoverable || 0),
    };
    const forecastYears = yearSnapshots.length > 0
        ? yearSnapshots.map((snap) => snap?.year).filter(Boolean)
        : (fallbackSnapshot?.year != null ? [fallbackSnapshot.year] : []);
    const forecastYearOptions = Array.from(
        new Set(
            (forecastYears || [])
                .map((year) => Number(year))
                .filter((year) => Number.isFinite(year))
        )
    ).sort((a, b) => a - b);
    const selectedForecastYearIndex = Math.max(
        0,
        forecastYearOptions.findIndex((year) => String(year) === String(selectedSnapshot?.year))
    );
    const seasonFilter = Array.isArray(caseData?.input_data?.season_filter) && caseData.input_data.season_filter.length > 0
        ? caseData.input_data.season_filter
        : ALL_SEASONS;
    const displayedCandidateStopChainCount = selectedSeason !== 'all'
        ? (savedDebugInfo?.season_counts || {})[selectedSeason] ?? activeDevData?.candidate_stop_chain_count
        : activeDevData?.candidate_stop_chain_count;
    const mapContext = caseData?.result_data?.map_context || {};
    const mapLayers = caseData?.map_layers || {};
    const publicChargers = normalizeMapLayerItems(mapLayers?.public_chargers);
    const mdhdInfrastructure = normalizeMapLayerItems(mapLayers?.mdhd_infrastructure);
    const warehouses = normalizeMapLayerItems(mapLayers?.warehouses);
    const truckStops = normalizeMapLayerItems(mapLayers?.truck_stops);
    const caseDisplayTitle = buildCaseDisplayTitle(caseData);

    return (
        <div className="min-h-screen bg-gray-50 font-ui p-8 space-y-6">
            <div className="flex justify-between items-start gap-4 pb-4 border-b-2 border-gray-200">
                <div className="flex items-start gap-4">
                    <button
                        onClick={() => navigate('/utility/scenarios')}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm font-ui font-medium"
                    >
                        <FaArrowLeft className="text-lg" />
                        <span>Back to Case Library</span>
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">{caseDisplayTitle}</h1>
                        <p className="text-gray-600 mt-1">{caseData.utility_name || '—'}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {caseData.forecast_scenario || 'MDHD load forecast'}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleDeleteCase}
                    disabled={deleting}
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition ${deleting
                        ? 'bg-red-100 text-red-400 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                >
                    {deleting ? 'Deleting…' : 'Delete Case'}
                </button>
            </div>

            {hasDevTab && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setActiveTab('overview')}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${activeTab === 'overview'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Case summary
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('dev')}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${activeTab === 'dev'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Dev access
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'overview' && (
                <>
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch">
                        <div className="xl:col-span-7 flex flex-col gap-4 min-w-0">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-full">
                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={showChargersOnMap}
                                            onChange={(e) => setShowChargersOnMap(e.target.checked)}
                                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                        />
                                        <MapLayerIcon type="public_chargers" />
                                        <span>Show public chargers</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={showMdhdInfrastructure}
                                            onChange={(e) => setShowMdhdInfrastructure(e.target.checked)}
                                            className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                                        />
                                        <MapLayerIcon type="mdhd_infrastructure" />
                                        <span>Show MDHD infrastructure</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={showWarehouses}
                                            onChange={(e) => setShowWarehouses(e.target.checked)}
                                            className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                        />
                                        <MapLayerIcon type="warehouses" />
                                        <span>Show warehouses</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={showTruckStops}
                                            onChange={(e) => setShowTruckStops(e.target.checked)}
                                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                        />
                                        <MapLayerIcon type="truck_stops" />
                                        <span>Show truck stops</span>
                                    </label>
                                    <div className="flex items-center gap-2 text-sm text-gray-700">
                                        <span>Heatmap layer</span>
                                        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden bg-white">
                                            <button
                                                type="button"
                                                onClick={() => setHeatmapLayerMetric('energy_delivered')}
                                                className={`px-2.5 py-1 text-xs font-medium ${heatmapLayerMetric === 'energy_delivered' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                Energy
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setHeatmapLayerMetric('peak_power_kw')}
                                                className={`px-2.5 py-1 text-xs font-medium border-l border-gray-300 ${heatmapLayerMetric === 'peak_power_kw' ? 'bg-violet-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                Peak power
                                            </button>
                                        </div>
                                    </div>
                                    {mapContext?.utility_territory_names?.length > 0 && (
                                        <span className="text-xs text-gray-500">
                                            Utility boundary: {mapContext.utility_territory_names.join(', ')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm flex-1 min-h-[620px] w-full">
                                <StopChainMap
                                    stops={[]}
                                    chargingStations={publicChargers}
                                    showChargerMarkers={showChargersOnMap}
                                    mdhdInfrastructure={mdhdInfrastructure}
                                    showMdhdInfrastructure={showMdhdInfrastructure}
                                    warehouses={warehouses}
                                    showWarehouses={showWarehouses}
                                    truckStops={truckStops}
                                    showTruckStops={showTruckStops}
                                    hexLoadAggregate={selectedHexes}
                                    showLoadHeatmap
                                    loadHeatmapMetric={heatmapLayerMetric}
                                    heatmapMaxValueOverride={heatmapMaxValueOverride}
                                    onHexagonClick={(hex) => setSelectedHexagonH3Index(String(hex?.h3_index || ''))}
                                    onClearHexagonSelection={() => setSelectedHexagonH3Index('')}
                                    selectedHexagonH3Index={selectedHexagonH3Index}
                                    selectedHexagonData={selectedHexagon}
                                    selectedForecastYear={selectedSnapshot?.year || null}
                                    utilityBoundaryGeometry={mapContext?.utility_boundary_geometry || null}
                                    showUtilityBoundary={Boolean(mapContext?.utility_boundary_geometry)}
                                    className="h-full"
                                />
                            </div>
                        </div>

                        <div className="xl:col-span-5 flex flex-col gap-4 min-w-0">
                            <div className="flex flex-col gap-4">
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                    <div className="text-sm font-semibold text-gray-800 mb-3">Forecast years</div>
                                    {yearSnapshots.length === 0 && (
                                        <div className="text-xs text-gray-500 mb-3">
                                            No forecast-year milestone was reached in this run.
                                        </div>
                                    )}
                                    {forecastYearOptions.length > 1 ? (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>{forecastYearOptions[0]}</span>
                                                <span className="font-medium text-gray-700">Selected: {selectedSnapshot?.year || '—'}</span>
                                                <span>{forecastYearOptions[forecastYearOptions.length - 1]}</span>
                                            </div>
                                            <input
                                                type="range"
                                                min={0}
                                                max={forecastYearOptions.length - 1}
                                                step={1}
                                                value={selectedForecastYearIndex}
                                                onChange={(e) => {
                                                    const idx = Number(e.target.value);
                                                    const nextYear = forecastYearOptions[idx];
                                                    if (nextYear != null) setSelectedYear(String(nextYear));
                                                }}
                                                className="w-full"
                                            />
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-600">
                                            {forecastYearOptions.length === 1 ? `Year ${forecastYearOptions[0]}` : 'No forecast years available.'}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                    <div className="text-sm font-semibold text-gray-800 mb-3">Season view</div>
                                    <div className="flex flex-wrap gap-2">
                                        {availableSeasonViews.map(({ key, label }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedSeason(key);
                                                    const nextResult = key !== 'all' && normalizedSeasonResults[key]
                                                        ? normalizedSeasonResults[key]
                                                        : (caseData?.result_data || {});
                                                    const nextSnapshots = Array.isArray(nextResult?.year_snapshots) ? nextResult.year_snapshots : [];
                                                    if (nextSnapshots.length > 0) {
                                                        setSelectedYear(String(nextSnapshots[nextSnapshots.length - 1]?.year ?? ''));
                                                    } else {
                                                        const nextFallback = buildFallbackSnapshot(nextResult);
                                                        setSelectedYear(nextFallback?.year != null ? String(nextFallback.year) : '');
                                                    }
                                                    setSelectedHexagonH3Index('');
                                                }}
                                                className={`px-3 py-1.5 rounded-md text-sm font-medium ${selectedSeason === key
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                                    }`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                        <div className="text-xs uppercase text-gray-500">Target EV count</div>
                                        <div className="text-2xl font-bold text-gray-800 mt-1">{selectedSnapshot?.target_ev_count ?? '—'}</div>
                                    </div>
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                        <div className="text-xs uppercase text-gray-500">Total load</div>
                                        <div className="text-2xl font-bold text-gray-800 mt-1">{totalMwh(selectedHexes).toFixed(2)} MWh</div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <div className="text-sm font-semibold text-gray-800">
                                        Utility-wide 8760 summary{selectedSnapshot?.year ? ` (${selectedSnapshot.year})` : ''}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setAnalyticsModalTarget('utility')}
                                        className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                                    >
                                        Open analytics
                                    </button>
                                </div>
                                {utilityWideProfileLoading ? (
                                    <div className="text-sm text-gray-500">Loading utility-wide profile...</div>
                                ) : utilityWideProfileError ? (
                                    <div className="text-sm text-red-600">{utilityWideProfileError}</div>
                                ) : !utilityWideProfile ? (
                                    <div className="text-sm text-gray-500">No utility-wide profile available.</div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                            <div className="rounded-md bg-gray-50 border border-gray-200 p-2.5">
                                                <div className="text-xs uppercase text-gray-500">Annual energy</div>
                                                <div className="mt-1 text-base font-semibold text-gray-800 whitespace-nowrap">{Number((utilityWideProfile?.annual_energy_kwh ?? 0) / 1000 || 0).toFixed(2)} MWh</div>
                                            </div>
                                            <div className="rounded-md bg-gray-50 border border-gray-200 p-2.5">
                                                <div className="text-xs uppercase text-gray-500">Yearly peak</div>
                                                <div className="mt-1 text-base font-semibold text-gray-800 whitespace-nowrap">{Number((utilityWideProfile?.peak_power_kw ?? 0) / 1000 || 0).toFixed(2)} MW</div>
                                            </div>
                                            <div className="rounded-md bg-gray-50 border border-gray-200 p-2.5">
                                                <div className="text-xs uppercase text-gray-500">Peak day</div>
                                                <div className="mt-1 text-base font-semibold text-gray-800 whitespace-nowrap">{utilityWideProfile?.peak_day_date || '—'}</div>
                                            </div>
                                        </div>
                                        <div className="rounded-md border border-dashed border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
                                            Open the analytics modal to inspect the utility-wide 8760-hour timeline, weekly load shape, seasonal daily profiles, and peak-day drilldown.
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex-1 min-h-[360px]">
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <div className="text-sm font-semibold text-gray-800">
                                        Selected hexagon details{selectedSnapshot?.year ? ` (${selectedSnapshot.year})` : ''}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {selectedHexagon && (
                                            <button
                                                type="button"
                                                onClick={() => setAnalyticsModalTarget('hex')}
                                                className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                                            >
                                                Open analytics
                                            </button>
                                        )}
                                        {selectedHexagon && (
                                            <button
                                                type="button"
                                                onClick={() => setSelectedHexagonH3Index('')}
                                                className="text-xs font-medium text-blue-700 hover:text-blue-800"
                                            >
                                                Clear selection
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {selectedHexagon ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-3 gap-2 text-sm">
                                            <div className="min-w-0 rounded-md bg-gray-50 border border-gray-200 p-2.5">
                                                <div className="text-xs uppercase text-gray-500">Hexagon</div>
                                                <div className="mt-1 font-mono text-[10px] leading-tight text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis">{selectedHexagon?.h3_index || '—'}</div>
                                            </div>
                                            <div className="min-w-0 rounded-md bg-gray-50 border border-gray-200 p-2.5">
                                                <div className="text-xs uppercase text-gray-500">Annual energy</div>
                                                <div className="mt-1 text-base font-semibold tracking-tight text-gray-800 whitespace-nowrap">{Number((selectedHexProfile?.annual_energy_kwh ?? selectedHexagon?.energy_delivered ?? 0) / 1000 || 0).toFixed(2)} MWh</div>
                                            </div>
                                            <div className="min-w-0 rounded-md bg-gray-50 border border-gray-200 p-2.5">
                                                <div className="text-xs uppercase text-gray-500">Yearly peak</div>
                                                <div className="mt-1 text-base font-semibold tracking-tight text-gray-800 whitespace-nowrap">{Number((selectedHexProfile?.peak_power_kw ?? selectedHexagon?.peak_power_kw ?? 0) / 1000 || 0).toFixed(2)} MW</div>
                                            </div>
                                        </div>
                                        <div className="rounded-md border border-dashed border-blue-200 bg-blue-50 p-3">
                                            {selectedHexProfileLoading ? (
                                                <div className="text-xs text-gray-500">Loading hex analytics...</div>
                                            ) : selectedHexProfileError ? (
                                                <div className="text-xs text-red-600">{selectedHexProfileError}</div>
                                            ) : (
                                                <div className="space-y-1">
                                                    <div className="text-xs font-semibold text-blue-900">Charts live in the analytics modal</div>
                                                    <div className="text-xs text-blue-800">
                                                        Open analytics to inspect the 8760-hour timeline, weekly load shape, seasonal average daily views, and peak-day profile for this hexagon.
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-full min-h-[280px] rounded-md border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-center px-6">
                                        <div>
                                            <div className="text-sm font-semibold text-gray-700">Select a hexagon to display details</div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                Click a heatmap hex on the map to view its annual energy and yearly peak, then open the analytics modal for the detailed profile views.
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'dev' && hasDevTab && (
                <div className="w-full overflow-x-hidden space-y-4">

                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
                        <div className="font-semibold text-slate-800 mb-2">Engine diagnostics (dev only)</div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span>requested: <span className="font-semibold">{engineDebug.requested}</span></span>
                            <span>used: <span className="font-semibold">{engineDebug.used}</span></span>
                            <span>variant: <span className="font-semibold">{engineDebug.engineVariant}</span></span>
                            <span>contract: <span className={`font-semibold ${engineDebug.contractOk === false ? 'text-red-700' : 'text-emerald-700'}`}>{engineDebug.contractOk === false ? 'mismatch' : 'ok'}</span></span>
                            {engineDebug.fallbackReason && (
                                <span>fallback: <span className="font-semibold text-amber-700">{engineDebug.fallbackReason}</span></span>
                            )}
                            {engineDebug.rolloutPercent != null && (
                                <span>rollout: <span className="font-semibold">{engineDebug.rolloutPercent}%</span></span>
                            )}
                            {engineDebug.rolloutBucket != null && (
                                <span>bucket: <span className="font-semibold">{engineDebug.rolloutBucket}</span></span>
                            )}
                            {engineDebug.rustEnabled != null && (
                                <span>rust enabled: <span className="font-semibold">{String(engineDebug.rustEnabled)}</span></span>
                            )}
                            {engineDebug.rustPointerEnabled != null && (
                                <span>rust pointer enabled: <span className="font-semibold">{String(engineDebug.rustPointerEnabled)}</span></span>
                            )}
                        </div>
                        {Object.keys(engineDebug.engineRuntimeStats || {}).length > 0 && (
                            <div className="mt-2">
                                runtime: <span className="font-mono text-[11px] break-all">{JSON.stringify(engineDebug.engineRuntimeStats)}</span>
                            </div>
                        )}
                        {Object.keys(engineDebug.hexProfileCoverage || {}).length > 0 && (
                            <div className="mt-2">
                                hex profile coverage: <span className="font-mono text-[11px] break-all">{JSON.stringify(engineDebug.hexProfileCoverage)}</span>
                            </div>
                        )}
                        {Object.keys(engineDebug.shortfallBackfill || {}).length > 0 && (
                            <div className="mt-2">
                                shortfall backfill: <span className="font-mono text-[11px] break-all">{JSON.stringify(engineDebug.shortfallBackfill)}</span>
                            </div>
                        )}
                        <div className="mt-3 rounded border border-slate-200 bg-white p-3">
                            <div className="text-xs font-semibold text-slate-800 mb-2">Forecast year selector (dev)</div>
                            {forecastYearOptions.length > 1 ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                                        <span>{forecastYearOptions[0]}</span>
                                        <span className="font-medium text-slate-700">Selected: {selectedSnapshot?.year || '—'}</span>
                                        <span>{forecastYearOptions[forecastYearOptions.length - 1]}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={0}
                                        max={forecastYearOptions.length - 1}
                                        step={1}
                                        value={selectedForecastYearIndex}
                                        onChange={(e) => {
                                            const idx = Number(e.target.value);
                                            const nextYear = forecastYearOptions[idx];
                                            if (nextYear != null) setSelectedYear(String(nextYear));
                                        }}
                                        className="w-full"
                                    />
                                </div>
                            ) : (
                                <div className="text-xs text-slate-600">
                                    {forecastYearOptions.length === 1 ? `Year ${forecastYearOptions[0]}` : 'No forecast years available.'}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                                <div className="text-sm font-semibold text-gray-800">Peak vehicle week attribution</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Identifies the forecast vehicle-week with the largest saved weekly peak-load signal.
                                </div>
                            </div>
                            <div className="text-xs text-gray-500">
                                {selectedSnapshot?.year ? `Year ${selectedSnapshot.year}` : 'No year selected'}
                            </div>
                        </div>
                        {!peakWeekAttribution ? (
                            <div className="text-sm text-gray-500">No weekly snapshots are available to attribute the peak load.</div>
                        ) : (
                            <div className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-sm">
                                    <div className="rounded-md bg-violet-50 border border-violet-200 p-3">
                                        <div className="text-xs uppercase text-violet-700">Peak week</div>
                                        <div className="text-lg font-semibold text-violet-950 mt-1">
                                            {peakWeekAttribution.weekSnapshot?.full_week_label || peakWeekAttribution.weekSnapshot?.week_label || `Week ${peakWeekAttribution.weekSnapshot?.week_index ?? '—'}`}
                                        </div>
                                    </div>
                                    <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                        <div className="text-xs uppercase text-gray-500">Season</div>
                                        <div className="text-lg font-semibold text-gray-800 mt-1 capitalize">{peakWeekAttribution.weekSnapshot?.season || '—'}</div>
                                    </div>
                                    <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                        <div className="text-xs uppercase text-gray-500">Weekly load</div>
                                        <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric(peakWeekAttribution.weekLoadMwh, 2, ' MWh')}</div>
                                    </div>
                                    <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                        <div className="text-xs uppercase text-gray-500">Peak signal</div>
                                        <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric(peakWeekAttribution.peakPowerKw / 1000, 2, ' MW')}</div>
                                    </div>
                                    <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                        <div className="text-xs uppercase text-gray-500">Load factor</div>
                                        <div className="text-lg font-semibold text-gray-800 mt-1">{peakWeekAttribution.loadFactor == null ? '—' : `${(peakWeekAttribution.loadFactor * 100).toFixed(1)}%`}</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs text-gray-700">
                                    <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                        <div>
                                            <span className="font-medium text-gray-600">Date range:</span>{' '}
                                            {formatWeekDateRange(peakWeekAttribution.weekSnapshot?.coverage_start_date || peakWeekAttribution.weekSnapshot?.week_start_date, peakWeekAttribution.weekSnapshot?.coverage_end_date || peakWeekAttribution.weekSnapshot?.week_end_date)}
                                        </div>
                                        <div className="mt-1">
                                            <span className="font-medium text-gray-600">Coverage:</span>{' '}
                                            {peakWeekAttribution.weekSnapshot?.coverage_days ?? '—'} day(s)
                                            {peakWeekAttribution.weekSnapshot?.is_partial_week ? ' (partial forecast week)' : ''}
                                        </div>
                                        <div className="mt-1">
                                            <span className="font-medium text-gray-600">Calendar alignment:</span>{' '}
                                            {peakWeekAttribution.weekSnapshot?.calendar_alignment || '—'}
                                        </div>
                                        <div className="mt-1">
                                            <span className="font-medium text-gray-600">Feasible / processed:</span>{' '}
                                            {peakWeekAttribution.weekSnapshot?.feasible_count ?? '—'} / {peakWeekAttribution.weekSnapshot?.total_processed ?? '—'}
                                        </div>
                                    </div>
                                    <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                        <div>
                                            <span className="font-medium text-gray-600">Top contributing hex:</span>{' '}
                                            <span className="font-mono break-all">{peakWeekAttribution.topHex?.h3_index || '—'}</span>
                                        </div>
                                        <div className="mt-1">
                                            <span className="font-medium text-gray-600">Top hex energy:</span>{' '}
                                            {formatMetric(Number(peakWeekAttribution.topHex?.energy_delivered || 0) / 1000, 3, ' MWh')}
                                        </div>
                                        <div className="mt-1">
                                            <span className="font-medium text-gray-600">Top hex peak:</span>{' '}
                                            {formatMetric(Number(peakWeekAttribution.topHex?.peak_power_kw || 0) / 1000, 3, ' MW')}
                                        </div>
                                    </div>
                                </div>
                                {Array.isArray(peakWeekAttribution.weekSnapshot?.feasible_chain_ids) && peakWeekAttribution.weekSnapshot.feasible_chain_ids.length > 0 && (
                                    <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                                        <span className="font-medium">Feasible stop-chain IDs captured for this week:</span>{' '}
                                        <span className="font-mono break-all">{peakWeekAttribution.weekSnapshot.feasible_chain_ids.slice(0, 20).join(', ')}</span>
                                        {peakWeekAttribution.weekSnapshot.feasible_chain_ids.length > 20 ? ' ...' : ''}
                                    </div>
                                )}
                                <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                                    This attributes the peak to a simulated forecast vehicle-week. It is not always a single historical route: the saved weekly peak signal is aggregated across charging stops/hexagons for that week.
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="text-sm font-semibold text-gray-800">
                                EPRI eRoadMAP validation
                            </div>
                            <div className="text-xs text-gray-500">
                                {selectedSnapshot?.year ? `Year ${selectedSnapshot.year}` : 'No year selected'}
                            </div>
                        </div>
                        {epriValidationLoading ? (
                            <div className="text-sm text-gray-500">Loading validation report...</div>
                        ) : epriValidationError ? (
                            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                {epriValidationError}
                            </div>
                        ) : !epriValidationReport ? (
                            <div className="text-sm text-gray-500">No validation report available.</div>
                        ) : (
                            <div className="space-y-4">
                                <div className="text-xs text-gray-500">
                                    Utility: <span className="font-semibold text-gray-700">{epriValidationReport?.utility_name || '—'}</span>
                                    {' | '}
                                    Resolution: <span className="font-semibold text-gray-700">R{epriValidationReport?.resolution ?? '—'}</span>
                                    {' | '}
                                    Mode: <span className="font-semibold text-gray-700">{epriValidationReport?.simulation_mode || '—'}</span>
                                </div>
                                {['managed', 'unmanaged'].map((trackKey) => {
                                    const track = epriValidationReport?.tracks?.[trackKey];
                                    if (!track) return null;
                                    const simulationPeakMw = Number(track?.totals?.simulation_peak_power_mw ?? 0);
                                    const benchmarkPeakMw = Number(track?.totals?.benchmark_peak_power_mw ?? 0);
                                    const peakDeltaMw = simulationPeakMw - benchmarkPeakMw;
                                    const peakPctDelta = benchmarkPeakMw > 0 ? (peakDeltaMw / benchmarkPeakMw) * 100 : null;
                                    const peakRatio = benchmarkPeakMw > 0 ? simulationPeakMw / benchmarkPeakMw : null;
                                    return (
                                        <div key={trackKey} className="rounded border border-gray-200 bg-gray-50 p-3">
                                            <div className="flex items-center justify-between gap-3 mb-2">
                                                <div className="text-sm font-semibold text-gray-800 capitalize">{trackKey} benchmark</div>
                                                <div className="text-xs text-gray-500">
                                                    matched {track?.matched_hex_count ?? 0} / benchmark {track?.benchmark_hex_count ?? 0}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-sm">
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    <div className="text-xs uppercase text-gray-500">Coverage</div>
                                                    <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric((track?.coverage_ratio ?? 0) * 100, 2, '%')}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    <div className="text-xs uppercase text-gray-500">Energy Weighted Mean Absolute Percent Error</div>
                                                    <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric(track?.metrics?.energy_weighted_mape_pct, 2, '%')}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    <div className="text-xs uppercase text-gray-500">Peak Weighted Mean Absolute Percent Error</div>
                                                    <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric(track?.metrics?.peak_weighted_mape_pct, 2, '%')}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    <div className="text-xs uppercase text-gray-500">Peak mean absolute error</div>
                                                    <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric(track?.metrics?.peak_mae_mw, 3, ' MW')}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    <div className="text-xs uppercase text-gray-500">Top-10 energy overlap</div>
                                                    <div className="text-lg font-semibold text-gray-800 mt-1">{track?.metrics?.top_10_energy_overlap_count ?? 0}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    <div className="text-xs uppercase text-gray-500">Top-10 power overlap</div>
                                                    <div className="text-lg font-semibold text-gray-800 mt-1">{track?.metrics?.top_10_peak_overlap_count ?? 0}</div>
                                                </div>
                                            </div>
                                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-600">
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    Simulation energy: <span className="font-semibold text-gray-800">{formatMetric(track?.totals?.simulation_energy_mwh_per_day, 3, ' MWh/day')}</span>
                                                    <br />
                                                    Benchmark energy: <span className="font-semibold text-gray-800">{formatMetric(track?.totals?.benchmark_energy_mwh_per_day, 3, ' MWh/day')}</span>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-white p-3">
                                                    Simulation peak sum: <span className="font-semibold text-gray-800">{formatMetric(track?.totals?.simulation_peak_power_mw, 3, ' MW')}</span>
                                                    <br />
                                                    Benchmark peak sum: <span className="font-semibold text-gray-800">{formatMetric(track?.totals?.benchmark_peak_power_mw, 3, ' MW')}</span>
                                                    <br />
                                                    Peak delta: <span className={`font-semibold ${peakDeltaMw > 0 ? 'text-red-700' : peakDeltaMw < 0 ? 'text-blue-700' : 'text-gray-800'}`}>
                                                        {formatMetric(peakDeltaMw, 3, ' MW')}
                                                    </span>
                                                    {peakPctDelta !== null ? (
                                                        <>
                                                            {' '}(<span className="font-semibold text-gray-800">{formatMetric(peakPctDelta, 2, '%')}</span>)
                                                        </>
                                                    ) : null}
                                                    <br />
                                                    Simulation / benchmark peak ratio: <span className="font-semibold text-gray-800">{peakRatio === null ? '—' : `${peakRatio.toFixed(2)}x`}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="text-[11px] text-gray-400 break-all">
                                    Dataset: {epriValidationReport?.dataset_path || '—'}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="text-sm font-semibold text-gray-800">
                                Weekly forecast for {selectedSnapshot?.year || 'selected year'}
                            </div>
                            <div className="text-xs text-gray-500">
                                {selectedWeekSnapshots.length > 0
                                    ? `${selectedWeekSnapshots.length} week snapshots`
                                    : 'No weekly snapshots saved for this year'}
                            </div>
                        </div>
                        {selectedWeekSnapshots.length === 0 ? (
                            <div className="text-sm text-gray-500">
                                This saved result does not include per-week forecast details for the selected year.
                            </div>
                        ) : (
                            <div className="max-h-[420px] overflow-auto rounded border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Week</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Season</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Date range</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Load (MWh)</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Hexagons</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Feasible</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Missing</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Resampled</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Backfill</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {selectedWeekSnapshots.map((weekSnapshot) => {
                                            const weekHexes = Array.isArray(weekSnapshot?.hex_load_aggregate)
                                                ? weekSnapshot.hex_load_aggregate
                                                : [];
                                            const weekLoadMwh = weekHexes.length > 0
                                                ? totalMwh(weekHexes)
                                                : Number(weekSnapshot?.week_total_load_mwh || 0);
                                            const weekHexCount = weekHexes.length > 0
                                                ? weekHexes.length
                                                : Number(weekSnapshot?.week_hexagon_count || 0);
                                            return (
                                                <tr key={`${weekSnapshot?.year}-${weekSnapshot?.week_index}-${weekSnapshot?.full_week_label || weekSnapshot?.week_label || 'week'}`}>
                                                    <td className="px-3 py-2 text-gray-800">
                                                        {weekSnapshot?.full_week_label || weekSnapshot?.week_label || `Week ${weekSnapshot?.week_index ?? '—'}`}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-700 capitalize">
                                                        {weekSnapshot?.season || '—'}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-700">
                                                        {formatWeekDateRange(weekSnapshot?.week_start_date, weekSnapshot?.week_end_date)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-gray-800">
                                                        {weekLoadMwh.toFixed(2)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-gray-800">
                                                        {weekHexCount}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-gray-800">
                                                        {weekSnapshot?.feasible_count ?? '—'}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-gray-800">
                                                        {weekSnapshot?.missing_feasible_count ?? 0}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-gray-800">
                                                        {weekSnapshot?.resampled_chain_count ?? 0}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                                                            weekSnapshot?.backfill_unrecoverable
                                                                ? 'bg-red-100 text-red-700'
                                                                : weekSnapshot?.backfill_applied
                                                                    ? 'bg-emerald-100 text-emerald-700'
                                                                    : 'bg-gray-100 text-gray-700'
                                                        }`}>
                                                            {formatBackfillStatusLabel(weekSnapshot)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Candidate stop chains</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">{displayedCandidateStopChainCount ?? '—'}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Processed stop chains</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">{activeDevData?.processed_stop_chain_count ?? '—'}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Feasible stop chains</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">{activeDevData?.feasible_stop_chain_count ?? '—'}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Trajectory preview</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">
                                {activeDevData?.trajectory_preview_count ?? 0} / {activeDevData?.trajectory_preview_limit ?? 100}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Year missing feasible</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">{selectedYearBackfillSummary.missingFeasibleCount}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Year resampled chains</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">{selectedYearBackfillSummary.resampledChainCount}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Weeks backfilled</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">{selectedYearBackfillSummary.weeksWithBackfill}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <div className="text-xs uppercase text-gray-500">Weeks unrecoverable</div>
                            <div className="text-2xl font-bold text-gray-800 mt-1">{selectedYearBackfillSummary.weeksUnrecoverable}</div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                        <div className="text-sm font-semibold text-gray-800 mb-3">Load sanity check</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Avg distance per feasible trajectory</div>
                                <div className="text-lg font-semibold text-gray-800 mt-1">
                                    {formatMetric(activeDevData?.load_sanity_check?.avg_distance_km, 2, ' km')}
                                </div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Estimated drive energy</div>
                                <div className="text-lg font-semibold text-gray-800 mt-1">
                                    {formatMetric(activeDevData?.load_sanity_check?.estimated_total_drive_energy_kwh, 1, ' kWh')}
                                </div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Predicted charging load</div>
                                <div className="text-lg font-semibold text-gray-800 mt-1">
                                    {formatMetric(activeDevData?.load_sanity_check?.predicted_total_load_kwh, 1, ' kWh')}
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Metric</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr>
                                        <td className="px-3 py-2 text-gray-700">Base drive energy assumption</td>
                                        <td className="px-3 py-2 text-right text-gray-800">{formatMetric(activeDevData?.load_sanity_check?.base_drive_energy_kwh_per_km, 3, ' kWh/km')}</td>
                                    </tr>
                                    <tr>
                                        <td className="px-3 py-2 text-gray-700">Avg drive energy per trajectory</td>
                                        <td className="px-3 py-2 text-right text-gray-800">{formatMetric(activeDevData?.load_sanity_check?.avg_drive_energy_kwh_per_trajectory, 2, ' kWh')}</td>
                                    </tr>
                                    <tr>
                                        <td className="px-3 py-2 text-gray-700">Difference</td>
                                        <td className="px-3 py-2 text-right text-gray-800">{formatMetric(activeDevData?.load_sanity_check?.difference_kwh, 1, ' kWh')}</td>
                                    </tr>
                                    <tr>
                                        <td className="px-3 py-2 text-gray-700">Difference percent</td>
                                        <td className="px-3 py-2 text-right text-gray-800">{formatMetric(activeDevData?.load_sanity_check?.difference_pct, 2, '%')}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {Object.keys(savedDebugInfo?.season_counts || {}).length > 0 && (
                            <div className="mt-4 text-xs text-gray-500">
                                Candidate vehicle-week counts by season: {Object.entries(savedDebugInfo.season_counts).map(([season, count]) => `${season}: ${count}`).join(' | ')}
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="text-sm font-semibold text-gray-800">Vehicle-week pattern diagnostics</div>
                            <div className="text-xs text-gray-500">
                                Built from saved trajectory preview rows in dev access
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Charging stops</div>
                                <div className="text-2xl font-bold text-gray-800 mt-1">{devPreviewDiagnostics.totalChargingStops}</div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Charging energy</div>
                                <div className="text-2xl font-bold text-gray-800 mt-1">{formatMetric(devPreviewDiagnostics.totalChargingEnergyKwh, 1, ' kWh')}</div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Avg charging stops / trajectory</div>
                                <div className="text-2xl font-bold text-gray-800 mt-1">{formatMetric(devPreviewDiagnostics.avgChargingStopsPerTrajectory, 2, '')}</div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Avg energy / charging stop</div>
                                <div className="text-2xl font-bold text-gray-800 mt-1">{formatMetric(devPreviewDiagnostics.avgEnergyPerChargingStopKwh, 2, ' kWh')}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                                <div className="text-sm font-semibold text-gray-800 mb-2">Utility TOU by hour</div>
                                <div className="text-xs text-gray-500 mb-2">
                                    Average hourly TOU price across charging stops saved in the trajectory preview.
                                </div>
                                <HourlyDiagnosticChart
                                    values={devPreviewDiagnostics.averageTouByHour}
                                    color="#7c3aed"
                                    yLabel="TOU price ($/kWh)"
                                    yFormatter={(value) => Number(value || 0).toFixed(2)}
                                />
                            </div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                                <div className="text-sm font-semibold text-gray-800 mb-2">Charging behavior by hour</div>
                                <div className="text-xs text-gray-500 mb-2">
                                    Charging energy grouped by stop arrival hour from the saved vehicle-week preview.
                                </div>
                                <HourlyDiagnosticChart
                                    values={devPreviewDiagnostics.hourlyChargeEnergy}
                                    color="#059669"
                                    yLabel="Charging energy (kWh)"
                                    yFormatter={(value) => Number(value || 0).toFixed(0)}
                                />
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                                <div className="text-sm font-semibold text-gray-800 mb-2">Charging split</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded border border-gray-200 bg-white p-3">
                                        <div className="text-xs uppercase text-gray-500">Depot charging stops</div>
                                        <div className="text-lg font-semibold text-gray-800 mt-1">{devPreviewDiagnostics.depotChargingStops}</div>
                                    </div>
                                    <div className="rounded border border-gray-200 bg-white p-3">
                                        <div className="text-xs uppercase text-gray-500">Public charging stops</div>
                                        <div className="text-lg font-semibold text-gray-800 mt-1">{devPreviewDiagnostics.publicChargingStops}</div>
                                    </div>
                                </div>
                                <div className="mt-3 text-xs text-gray-500">
                                    Trajectories with charging activity: <span className="font-semibold text-gray-700">{devPreviewDiagnostics.trajectoriesWithCharging}</span>
                                </div>
                            </div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                                <div className="text-sm font-semibold text-gray-800 mb-2">TOU / utility coverage</div>
                                <div className="space-y-2 text-xs">
                                    <div>
                                        <span className="font-medium text-gray-700">Top utilities:</span>{' '}
                                        {devPreviewDiagnostics.topUtilities.length > 0
                                            ? devPreviewDiagnostics.topUtilities.map(([label, count]) => `${label} (${count})`).join(' | ')
                                            : '—'}
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">Top rates:</span>{' '}
                                        {devPreviewDiagnostics.topRates.length > 0
                                            ? devPreviewDiagnostics.topRates.map(([label, count]) => `${label} (${count})`).join(' | ')
                                            : '—'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="text-sm font-semibold text-gray-800">Temperature sanity checks</div>
                            <div className="text-xs text-gray-500">
                                Seasonal summaries from saved stop-weather preview data (with DB averaged-weather fallback)
                            </div>
                        </div>
                        <div className="overflow-x-auto rounded border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Season</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Avg segment temp</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Min</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Max</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Samples</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Source</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {['winter', 'spring', 'summer', 'fall'].map((season) => {
                                        const previewSummary = devPreviewDiagnostics.seasonalTemperatureSummary?.[season] || {};
                                        const dbSummary = weatherDatabaseSummary?.seasonal_summary?.[season] || {};
                                        const hasPreviewSamples = Number(previewSummary.sampleCount || 0) > 0;
                                        const summary = hasPreviewSamples
                                            ? previewSummary
                                            : {
                                                avgF: dbSummary.avg_temp_f,
                                                minF: dbSummary.min_temp_f,
                                                maxF: dbSummary.max_temp_f,
                                                sampleCount: Number(dbSummary.row_count || 0),
                                            };
                                        return (
                                            <tr key={`temp-${season}`}>
                                                <td className="px-3 py-2 text-gray-800 capitalize">{season}</td>
                                                <td className="px-3 py-2 text-right text-gray-800">{formatMetric(summary.avgF, 1, ' F')}</td>
                                                <td className="px-3 py-2 text-right text-gray-800">{formatMetric(summary.minF, 1, ' F')}</td>
                                                <td className="px-3 py-2 text-right text-gray-800">{formatMetric(summary.maxF, 1, ' F')}</td>
                                                <td className="px-3 py-2 text-right text-gray-800">{summary.sampleCount ?? 0}</td>
                                                <td className="px-3 py-2 text-xs text-gray-600">{hasPreviewSamples ? 'trajectory preview' : 'weather db averaged buckets'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {weatherDatabaseSummary && (
                            <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                                Weather DB rows: <span className="font-semibold">{weatherDatabaseSummary?.row_count ?? 0}</span>
                                {' | '}
                                Stations: <span className="font-semibold">{weatherDatabaseSummary?.station_count ?? 0}</span>
                                {' | '}
                                Utility-filtered stations: <span className="font-semibold">{weatherDatabaseSummary?.resolved_station_count_for_utility ?? 0}</span>
                                {' | '}
                                Overall avg: <span className="font-semibold">{formatMetric(weatherDatabaseSummary?.temperature?.avg_temp_f, 1, ' F')}</span>
                            </div>
                        )}
                        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                            Use this table to sanity-check whether the saved multi-year averaged weather buckets look too cold for the selected utility. If Moreno Valley still looks unexpectedly low here after a fresh run, the next place to inspect is the weather resolver’s station selection and averaged bucket source rows.
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                                <div className="text-sm font-semibold text-gray-800">Charging stop profile drilldown</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Each orange marker is one simulated charging stop with one stored charging profile. Full curves are fetched on click.
                                </div>
                            </div>
                            <div className="text-xs text-gray-500">
                                {selectedSnapshot?.year ? `Year ${selectedSnapshot.year}` : 'No year selected'}
                            </div>
                        </div>
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-medium uppercase text-gray-500">Season</span>
                                {DAILY_PROFILE_SEASONS.map((seasonKey) => (
                                    <button
                                        key={`stop-profile-season-${seasonKey}`}
                                        type="button"
                                        onClick={() => setStopProfileSeasonFilter(seasonKey)}
                                        className={`px-2.5 py-1 rounded-md text-xs font-medium ${stopProfileSeasonFilter === seasonKey
                                            ? 'bg-orange-600 text-white'
                                            : 'bg-orange-50 text-orange-800 hover:bg-orange-100'
                                            }`}
                                    >
                                        {seasonKey === 'all' ? 'All' : seasonKey.charAt(0).toUpperCase() + seasonKey.slice(1)}
                                    </button>
                                ))}
                            </div>
                            {mapContext?.utility_territory_names?.length > 0 && (
                                <div className="text-xs text-gray-500">
                                    Boundary: {mapContext.utility_territory_names.join(', ')}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 text-sm">
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Charging stops</div>
                                <div className="text-lg font-semibold text-gray-800 mt-1">{stopProfileSummary.count}</div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Profile energy</div>
                                <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric(stopProfileSummary.totalEnergyKwh / 1000, 3, ' MWh')}</div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Depot / public</div>
                                <div className="text-lg font-semibold text-gray-800 mt-1">{stopProfileSummary.depotCount} / {stopProfileSummary.publicCount}</div>
                            </div>
                            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                                <div className="text-xs uppercase text-gray-500">Max stop peak</div>
                                <div className="text-lg font-semibold text-gray-800 mt-1">{formatMetric(stopProfileSummary.peakPowerKw, 1, ' kW')}</div>
                            </div>
                        </div>
                        {stopProfilesError && (
                            <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                {stopProfilesError}
                            </div>
                        )}
                        {stopProfilesMeta?.profile_year_fallback && (
                            <div className="mb-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                                Showing representative dev-preview stop profiles from {stopProfilesMeta.effective_forecast_year} because no stop-profile rows were saved for {stopProfilesMeta.requested_forecast_year}.
                            </div>
                        )}
                        {stopProfilesMeta?.available_profile_seasons?.length > 0 && (
                            <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                                Saved stop-profile seasons: <span className="font-semibold">{stopProfilesMeta.available_profile_seasons.join(', ')}</span>.
                                {' '}These rows come from the bounded dev-preview sampler, while the seasonal load analytics above come from the full simulation aggregate.
                            </div>
                        )}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white h-[420px]">
                                <StopChainMap
                                    stops={selectedPreview?.stops || []}
                                    selectedStopIndex={0}
                                    chargingStations={[]}
                                    showChargerMarkers={false}
                                    hexLoadAggregate={[]}
                                    showLoadHeatmap={false}
                                    chargingStopProfiles={stopProfiles}
                                    selectedStopProfileId={selectedStopProfileId}
                                    onStopProfileClick={(point) => setSelectedStopProfileId(String(point?.id || ''))}
                                    utilityBoundaryGeometry={mapContext?.utility_boundary_geometry || null}
                                    showUtilityBoundary={Boolean(mapContext?.utility_boundary_geometry)}
                                    className="h-full"
                                />
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 min-h-[420px]">
                                <div className="text-sm font-semibold text-gray-800 mb-3">Selected stop charging profile</div>
                                {stopProfilesLoading ? (
                                    <div className="text-sm text-gray-500">Loading charging stop markers...</div>
                                ) : selectedStopProfileLoading ? (
                                    <div className="text-sm text-gray-500">Loading charging profile...</div>
                                ) : selectedStopProfileError ? (
                                    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{selectedStopProfileError}</div>
                                ) : !selectedStopProfile ? (
                                    <div className="h-full min-h-[300px] rounded border border-dashed border-gray-300 bg-white flex items-center justify-center px-4 text-center text-sm text-gray-500">
                                        Select an orange charging stop marker on the map to fetch its full pointwise charging profile.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div><span className="text-gray-500">Stop chain:</span> <span className="font-semibold text-gray-800">{selectedStopProfile.stop_chain_id ?? '—'}</span></div>
                                            <div><span className="text-gray-500">Stop:</span> <span className="font-semibold text-gray-800">{selectedStopProfile.stop_index ?? '—'}</span></div>
                                            <div><span className="text-gray-500">Energy:</span> <span className="font-semibold text-gray-800">{formatMetric(selectedStopProfile.energy_delivered_kwh, 2, ' kWh')}</span></div>
                                            <div><span className="text-gray-500">Peak:</span> <span className="font-semibold text-gray-800">{formatMetric(selectedStopProfile.peak_power_kw, 1, ' kW')}</span></div>
                                            <div><span className="text-gray-500">Duration:</span> <span className="font-semibold text-gray-800">{formatMetric(selectedStopProfile.duration_minutes, 1, ' min')}</span></div>
                                            <div><span className="text-gray-500">Type:</span> <span className="font-semibold text-gray-800">{selectedStopProfile.is_depot ? 'Depot' : 'Public/enroute'}</span></div>
                                            <div className="col-span-2"><span className="text-gray-500">Rate:</span> <span className="font-semibold text-gray-800">{selectedStopProfile.tou_rate_name || '—'}</span></div>
                                            <div className="col-span-2"><span className="text-gray-500">Source:</span> <span className="font-semibold text-gray-800">{selectedStopProfile.profile_source || '—'}</span></div>
                                        </div>
                                        <StopChargingProfileChart profilePoints={selectedStopProfile.charging_profile || []} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mt-3 rounded border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900">
                            Interpret clusters as repeated charging demand at nearby stops, depots, corridors, or industrial zones. Dense clusters can mean many simulated vehicles charged there, a few high-energy stops overlap spatially, or preview sampling selected repeated route patterns; use marker count, MWh, depot/public split, and selected-stop profiles together before treating a cluster as a required station site.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 xl:col-span-1">
                            <div className="text-sm font-semibold text-gray-800 mb-2">Iteration trace</div>
                            {Array.isArray(activeDevData?.iteration_trace_stop_chain_ids) && activeDevData.iteration_trace_stop_chain_ids.length > 0 ? (
                                <>
                                    <div className="text-xs text-gray-500 mb-2">
                                        First {activeDevData?.iteration_trace_count ?? activeDevData.iteration_trace_stop_chain_ids.length} iterated `stop_chain_id` values.
                                    </div>
                                    <div className="max-h-[320px] overflow-auto rounded border border-gray-200">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50 sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">#</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Stop chain ID</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {activeDevData.iteration_trace_stop_chain_ids.map((chainId, index) => (
                                                    <tr key={`iteration-trace-${chainId}-${index}`}>
                                                        <td className="px-3 py-1.5 text-gray-700">{index + 1}</td>
                                                        <td className="px-3 py-1.5 font-mono text-gray-800">{chainId}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div className="text-sm text-gray-500">No iteration trace was saved for this run.</div>
                            )}
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 xl:col-span-2">
                            <div className="text-sm font-semibold text-gray-800 mb-2">Trajectory preview</div>
                            {trajectoryPreview.length === 0 ? (
                                <div className="text-sm text-gray-500">No dev trajectory preview was saved for this case.</div>
                            ) : (
                                <div className="max-h-[320px] overflow-auto rounded border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">#</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Week</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Truck</th>
                                                <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Stops</th>
                                                <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Distance</th>
                                                <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {trajectoryPreview.map((row, index) => (
                                                <tr
                                                    key={`${row?.stop_chain_id || index}`}
                                                    onClick={() => setSelectedPreviewIndex(index)}
                                                    className={`cursor-pointer ${selectedPreviewIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                >
                                                    <td className="px-3 py-1.5 text-gray-700">{index + 1}</td>
                                                    <td className="px-3 py-1.5 text-gray-800">{row?.week || '—'}</td>
                                                    <td className="px-3 py-1.5 text-gray-800">{row?.truck_id || row?.truck_file || '—'}</td>
                                                    <td className="px-3 py-1.5 text-right text-gray-800">{row?.num_stops ?? '—'}</td>
                                                    <td className="px-3 py-1.5 text-right text-gray-800">{formatMetric(row?.total_distance_km, 2, ' km')}</td>
                                                    <td className="px-3 py-1.5 text-right text-gray-800">{formatMetric(row?.total_cost, 2, '') === '—' ? '—' : `$${Number(row?.total_cost || 0).toFixed(2)}`}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedPreview && (
                        <>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <div className="text-sm font-semibold text-gray-800 mb-3">Selected trajectory map</div>
                                <div className="rounded-lg overflow-hidden border border-gray-200 bg-white h-[420px]">
                                    <StopChainMap
                                        stops={selectedPreview?.stops || []}
                                        chargingStations={[]}
                                        showChargerMarkers={false}
                                        hexLoadAggregate={[]}
                                        showLoadHeatmap={false}
                                        className="h-full"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                    <div className="text-sm font-semibold text-gray-800 mb-3">Selected trajectory</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-gray-500">Stop chain:</span> <span className="text-gray-800">{selectedPreview?.stop_chain_id ?? '—'}</span></div>
                                        <div><span className="text-gray-500">Truck:</span> <span className="text-gray-800">{selectedPreview?.truck_id || selectedPreview?.truck_file || '—'}</span></div>
                                        <div><span className="text-gray-500">Stops:</span> <span className="text-gray-800">{selectedPreview?.num_stops ?? '—'}</span></div>
                                        <div><span className="text-gray-500">Distance:</span> <span className="text-gray-800">{formatMetric(selectedPreview?.total_distance_km, 2, ' km')}</span></div>
                                        <div><span className="text-gray-500">Total cost:</span> <span className="text-gray-800">{selectedPreview?.total_cost != null ? `$${Number(selectedPreview.total_cost).toFixed(2)}` : '—'}</span></div>
                                        <div><span className="text-gray-500">Status:</span> <span className="text-gray-800">{selectedPreview?.status || '—'}</span></div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                    <div className="text-sm font-semibold text-gray-800 mb-3">TOU information</div>
                                    {Array.isArray(selectedPreview?.charging_stop_tou) && selectedPreview.charging_stop_tou.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Stop</th>
                                                        <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Utility</th>
                                                        <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Rate</th>
                                                        <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Energy</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {selectedPreview.charging_stop_tou.map((touRow, index) => (
                                                        <tr key={`tou-${index}`}>
                                                            <td className="px-3 py-2 text-gray-800">{touRow?.stop_index ?? '—'}</td>
                                                            <td className="px-3 py-2 text-gray-800">{touRow?.utility_region || '—'}</td>
                                                            <td className="px-3 py-2 text-gray-700">
                                                                <div>{touRow?.tou_rate_name || 'Default TOU'}</div>
                                                                <div className="text-xs text-gray-500 mt-1">{formatTouPeriodLabel(touRow?.tou)}</div>
                                                            </td>
                                                            <td className="px-3 py-2 text-right text-gray-800">{formatMetric(touRow?.energy_delivered, 2, ' kWh')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-500">No TOU usage was saved for this trajectory.</div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <div className="text-sm font-semibold text-gray-800 mb-3">Weather data</div>
                                {Array.isArray(selectedPreview?.stop_weather) && selectedPreview.stop_weather.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Stop</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Arrival (PT)</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Departure (PT)</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Arrival temp (F / C)</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Departure temp (F / C)</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Avg temp to next stop (F / C)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {selectedPreview.stop_weather.map((entry, index) => (
                                                    <tr key={`weather-${index}`}>
                                                        <td className="px-3 py-2 text-gray-800">{entry?.stop_index ?? index}</td>
                                                        <td className="px-3 py-2 text-gray-800">{formatPacificTime(entry?.arrival_time)}</td>
                                                        <td className="px-3 py-2 text-gray-800">{formatPacificTime(entry?.departure_time)}</td>
                                                        <td className="px-3 py-2 text-right text-gray-800">
                                                            {formatMetric(entry?.arrival_temperature_f, 1, ' F')} / {formatMetric(entry?.arrival_temperature_c, 1, ' C')}
                                                        </td>
                                                        <td className="px-3 py-2 text-right text-gray-800">
                                                            {formatMetric(entry?.departure_temperature_f, 1, ' F')} / {formatMetric(entry?.departure_temperature_c, 1, ' C')}
                                                        </td>
                                                        <td className="px-3 py-2 text-right text-gray-800">
                                                            {formatMetric(entry?.segment_avg_temp_to_next_f, 1, ' F')} / {formatMetric(entry?.segment_avg_temp_to_next_c, 1, ' C')}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500">No weather data was saved for this trajectory.</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            <ProfileAnalyticsModal
                open={Boolean(analyticsModalTarget)}
                onClose={() => setAnalyticsModalTarget(null)}
                title={activeAnalyticsTitle}
                profile={activeAnalyticsProfile}
                loading={activeAnalyticsLoading}
                error={activeAnalyticsError}
                seasonView={analyticsSeasonView}
                onSeasonViewChange={setAnalyticsSeasonView}
                dayTypeView={analyticsDayTypeView}
                onDayTypeViewChange={setAnalyticsDayTypeView}
            />
        </div>
    );
}

export default UtilityScenarioCaseDetailPage;
